import { VILNIUS_LANDMARK_REPLACED_BUILDING_ID_VALUES } from "./landmarkFootprints.generated.ts";
import { VILNIUS_MODULAR_CITY_REPLACED_BUILDING_ID_VALUES } from "./modularCityFootprints.generated.ts";

export const VILNIUS_LANDMARK_MANIFEST_URL =
  "/assets/landmarks/manifest.json";

/**
 * Keep landmark startup bounded once the city contains several dozen authored
 * GLBs. Each entry fetches its full and simplified visual levels in parallel.
 */
export const VILNIUS_LANDMARK_ASSET_LOAD_CONCURRENCY = 4;

/** Decode one distant landmark at a time so background work stays interactive. */
export const VILNIUS_AUTHORED_ASSET_BACKGROUND_LOAD_CONCURRENCY = 1;
export const VILNIUS_LANDMARK_LOD_HYSTERESIS = 0.08;

/**
 * Only authored assets close enough to matter at the initial street bookmark
 * block first play. The rest are filled in after the first playable frame.
 *
 * This radius keeps Cathedral Square, its skyline anchors, the parked vehicle
 * review set, and the nearby traversal gates present at spawn without making
 * the airport and the rest of the city part of the critical download.
 */
export const VILNIUS_AUTHORED_ASSET_INITIAL_RADIUS_M = 320;

/** Give first interaction priority before distant GLBs start decoding. */
export const VILNIUS_AUTHORED_ASSET_BACKGROUND_START_DELAY_MS = 1_000;

/** Yield between background GLB batches to avoid one continuous decode task. */
export const VILNIUS_AUTHORED_ASSET_BACKGROUND_BATCH_DELAY_MS = 50;

/**
 * Begin fetching an authored asset before its origin reaches the fog horizon.
 * This hides normal network/decode latency without loading the rest of Vilnius.
 */
export const VILNIUS_AUTHORED_ASSET_STREAMING_PREFETCH_MARGIN_M = 160;

export interface AuthoredLandmarkBoxCollider {
  readonly kind: "box";
  readonly id: string;
  readonly centerLocalM: Readonly<{ x: number; y: number; z: number }>;
  readonly sizeM: Readonly<{ x: number; y: number; z: number }>;
  readonly rotationXRadians?: number;
  readonly rotationYRadians?: number;
}

export interface AuthoredLandmarkCylinderCollider {
  readonly kind: "cylinder";
  readonly id: string;
  readonly centerLocalM: Readonly<{ x: number; y: number; z: number }>;
  readonly heightM: number;
  readonly radiusM: number;
}

export type AuthoredLandmarkCollider =
  | AuthoredLandmarkBoxCollider
  | AuthoredLandmarkCylinderCollider;

export interface AuthoredLandmarkCollisionSpec {
  readonly landmarkId: string;
  readonly tileId: `${number}:${number}`;
  readonly buildingId: string;
  readonly origin: Readonly<{ x: number; y: number; z: number }>;
  readonly rotationYRadians: number;
  readonly colliders: readonly AuthoredLandmarkCollider[];
}

export interface AuthoredLandmarkTerrainCutout {
  readonly id: string;
  readonly landmarkId: string;
  readonly centerWorldM: Readonly<{ x: number; z: number }>;
  readonly sizeM: Readonly<{ x: number; z: number }>;
  readonly rotationYRadians: number;
}

export interface AuthoredInteriorSurfaceMask {
  readonly id: string;
  readonly tileId: `${number}:${number}`;
  readonly outerWorldM: readonly Readonly<{ x: number; z: number }>[];
  readonly transportFeatureIds: ReadonlySet<string>;
}

const landmarkTerrainCutout = (
  id: string,
  landmarkId: string,
  origin: Readonly<{ x: number; z: number }>,
  rotationYRadians: number,
  centerLocalM: Readonly<{ x: number; z: number }>,
  sizeM: Readonly<{ x: number; z: number }>,
): AuthoredLandmarkTerrainCutout => {
  const cosine = Math.cos(rotationYRadians);
  const sine = Math.sin(rotationYRadians);
  return Object.freeze({
    id,
    landmarkId,
    centerWorldM: Object.freeze({
      x: origin.x + centerLocalM.x * cosine + centerLocalM.z * sine,
      z: origin.z - centerLocalM.x * sine + centerLocalM.z * cosine,
    }),
    sizeM: Object.freeze({ ...sizeM }),
    rotationYRadians,
  });
};

/**
 * Narrow collision-only terrain openings for source-backed below-grade rooms.
 * Their authored floors remain the sole walkable surface inside each opening.
 */
export const VILNIUS_AUTHORED_LANDMARK_TERRAIN_CUTOUTS: readonly AuthoredLandmarkTerrainCutout[] =
  Object.freeze([
    landmarkTerrainCutout(
      "cathedral-crypt",
      "vilnius-cathedral",
      { x: 634, z: 267 },
      0,
      { x: 29.7, z: 13.3 },
      { x: 13.0, z: 30.0 },
    ),
    landmarkTerrainCutout(
      "gediminas-tower-floor",
      "gediminas-castle",
      { x: 820.637, z: 160.529 },
      12.3 * Math.PI / 180,
      { x: -0.91, z: 6.17 },
      { x: 12.0, z: 12.0 },
    ),
    landmarkTerrainCutout(
      "grand-dukes-archaeology",
      "palace-grand-dukes",
      { x: 720.396, z: 224.592 },
      0.348004,
      { x: -8, z: 45.5 },
      { x: 80, z: 17.5 },
    ),
  ]);

/** Collision tessellation used only by tiles intersecting a basement cutout. */
export const VILNIUS_AUTHORED_LANDMARK_TERRAIN_CUTOUT_GRID_M = 2;

export function authoredLandmarkTerrainCutoutsIntersecting(
  bounds: Readonly<{
    minX: number;
    minZ: number;
    maxX: number;
    maxZ: number;
  }>,
): readonly AuthoredLandmarkTerrainCutout[] {
  return VILNIUS_AUTHORED_LANDMARK_TERRAIN_CUTOUTS.filter((cutout) => {
    const cosine = Math.abs(Math.cos(cutout.rotationYRadians));
    const sine = Math.abs(Math.sin(cutout.rotationYRadians));
    const halfX = cutout.sizeM.x / 2 * cosine + cutout.sizeM.z / 2 * sine;
    const halfZ = cutout.sizeM.x / 2 * sine + cutout.sizeM.z / 2 * cosine;
    return cutout.centerWorldM.x + halfX >= bounds.minX &&
      cutout.centerWorldM.x - halfX <= bounds.maxX &&
      cutout.centerWorldM.z + halfZ >= bounds.minZ &&
      cutout.centerWorldM.z - halfZ <= bounds.maxZ;
  });
}

export function authoredLandmarkTerrainCutoutContains(
  cutout: Readonly<AuthoredLandmarkTerrainCutout>,
  point: Readonly<{ x: number; z: number }>,
): boolean {
  const deltaX = point.x - cutout.centerWorldM.x;
  const deltaZ = point.z - cutout.centerWorldM.z;
  const cosine = Math.cos(cutout.rotationYRadians);
  const sine = Math.sin(cutout.rotationYRadians);
  const localX = deltaX * cosine - deltaZ * sine;
  const localZ = deltaX * sine + deltaZ * cosine;
  return Math.abs(localX) <= cutout.sizeM.x / 2 &&
    Math.abs(localZ) <= cutout.sizeM.z / 2;
}

/**
 * Source footprints remain in collision and geographic data. Only their
 * generic procedural render representations are replaced by authored landmark
 * GLBs or verified standalone modular buildings.
 */
export const VILNIUS_LANDMARK_REPLACED_BUILDING_IDS: ReadonlySet<string> =
  new Set([
    ...VILNIUS_LANDMARK_REPLACED_BUILDING_ID_VALUES,
    ...VILNIUS_MODULAR_CITY_REPLACED_BUILDING_ID_VALUES,
  ]);

export function isAuthoredLandmarkFootprint(buildingId: string): boolean {
  return VILNIUS_LANDMARK_REPLACED_BUILDING_IDS.has(buildingId);
}

const TOWN_HALL_BUILDING_ID =
  "grpk:F37142D2-DC0C-48E1-9904-16D254357E29";
const CATHEDRAL_BUILDING_ID =
  "grpk:DFA25394-3E0C-4A7C-A7FF-895C887CFC0A";
const LUKISKES_PRISON_MAIN_BUILDING_ID =
  "grpk:BB0D4953-2583-4E9F-A27A-F25E9C772CB5";
const LUKISKES_PRISON_EAST_BUILDING_ID =
  "grpk:F6095813-14D8-417A-9258-AF08A9764406";
const LUKISKES_PRISON_WEST_BUILDING_ID =
  "grpk:3A761A11-25A9-4830-A027-65D97481332B";
const VILNIUS_TV_TOWER_BUILDING_ID =
  "grpk:35F941A7-4628-4213-A5C9-9CF9B1CC33F2";
const LIETUVOS_BANKAS_BUILDING_ID =
  "grpk:35ab31bc-b262-480a-bb78-42995c878422";
const GEDIMINO_9_BUILDING_ID =
  "grpk:6E23B88E-EDD4-4A39-874E-ABDD2EA1E6C9";
const HALES_MARKET_BUILDING_ID =
  "grpk:2E260EC5-F7FD-4756-9C7D-855C8A2FF8AB";
const MINDAUGO_MAXIMA_BUILDING_ID =
  "grpk:35738098-CADC-4A0C-B503-18A37A44248F";
const KABLYS_BUILDING_ID =
  "grpk:8CE71B97-A35D-4C97-BFB4-9D96944DB87E";
const DEMOKRATINE_MOKYKLA_BUILDING_ID =
  "grpk:08b48536-19f7-4c48-a174-15ef0f381712";
const SALOMEJOS_NERIES_GIMNAZIJA_BUILDING_ID =
  "grpk:1E081770-CCB8-46C5-8D8C-A73243F2173D";
const VILNIUS_RAILWAY_STATION_BUILDING_ID =
  "grpk:1A3AE562-C0CB-4504-88B4-D7AF4CA1B12A";
const VILNIUS_BUS_STATION_BUILDING_ID =
  "grpk:D9C504E3-ACED-462B-A79E-A70F84E7933D";
const VILNIUS_AIRPORT_TERMINAL_BUILDING_ID =
  "grpk:8DB583A0-A1A5-4417-8465-BBB158933F45";
const PRESIDENTIAL_PALACE_BUILDING_ID =
  "grpk:37A9D2C2-EA97-43C7-BF65-49E15F2A1C88";
const GEDIMINAS_CASTLE_TOWER_BUILDING_ID =
  "grpk:5F630807-1418-432B-A2DD-06F2C399B242";
const GRAND_DUKES_PALACE_BUILDING_ID =
  "grpk:20758473-25E7-443A-88CC-FF5026C4E18E";
const GATE_OF_DAWN_BUILDING_ID =
  "grpk:58f353a1-513e-45bd-9105-1158dca6478b";
const VILNIUS_UNIVERSITY_LIBRARY_BUILDING_ID =
  "grpk:829DBA27-52DB-4556-86F0-2B8882D22923";

/** Official GRPK outline transformed into the palace asset's local axes. */
const PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M = Object.freeze([
  [13.927, -50.307],
  [15.363, -46.239],
  [10.490, -45.250],
  [13.113, -33.260],
  [0.266, -29.769],
  [5.055, -11.798],
  [8.306, -12.664],
  [13.491, 7.854],
  [10.505, 8.649],
  [15.052, 25.709],
  [23.048, 23.949],
  [25.852, 37.583],
  [-2.881, 44.102],
  [-6.419, 29.423],
  [-3.815, 28.797],
  [-6.978, 15.661],
  [-9.533, 16.276],
  [-14.638, -5.314],
  [-12.173, -5.908],
  [-15.778, -20.877],
  [-18.190, -20.323],
  [-21.382, -34.217],
  [6.022, -46.238],
  [13.246, -51.102],
] as const);
const WHITE_BRIDGE_FOOD_HALL_BUILDING_ID =
  "grpk:27D50581-FC8A-44EA-AA60-B508B2E6DBBB";

const WHITE_BRIDGE_FOOD_HALL_OUTLINE_LOCAL_M = Object.freeze([
  [84.466, 21.157],
  [81.042, 22.556],
  [75.873, 3.257],
  [75.77, -2.982],
  [80.604, -21.915],
  [83.594, -20.759],
  [86.676, -19.092],
  [89.414, -16.738],
  [91.748, -14.091],
  [93.251, -11.211],
  [94.495, -6.977],
  [94.712, -3.297],
  [94.816, 2.94],
  [94.549, 5.944],
  [94.005, 9.197],
  [92.331, 13.254],
  [90.441, 16.092],
  [87.816, 18.778],
] as const);
const WHITE_BRIDGE_FOOD_HALL_ENTRANCE_EDGES = new Set([2, 11]);
const WHITE_BRIDGE_FOOD_HALL_FLOOR_Y = -0.087;
const WHITE_BRIDGE_FOOD_HALL_CEILING_Y = 4.183;

function whiteBridgeFoodHallWallColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  for (
    let index = 0;
    index < WHITE_BRIDGE_FOOD_HALL_OUTLINE_LOCAL_M.length;
    index += 1
  ) {
    const start = WHITE_BRIDGE_FOOD_HALL_OUTLINE_LOCAL_M[index]!;
    const end = WHITE_BRIDGE_FOOD_HALL_OUTLINE_LOCAL_M[
      (index + 1) % WHITE_BRIDGE_FOOD_HALL_OUTLINE_LOCAL_M.length
    ]!;
    const deltaX = end[0] - start[0];
    const deltaZ = end[1] - start[1];
    const isEntrance = WHITE_BRIDGE_FOOD_HALL_ENTRANCE_EDGES.has(index);
    const height = isEntrance
      ? WHITE_BRIDGE_FOOD_HALL_CEILING_Y -
        (WHITE_BRIDGE_FOOD_HALL_FLOOR_Y + 2.8)
      : WHITE_BRIDGE_FOOD_HALL_CEILING_Y -
        WHITE_BRIDGE_FOOD_HALL_FLOOR_Y;
    colliders.push({
      kind: "box",
      id: isEntrance ? `entrance-lintel-${index}` : `facade-${index}`,
      centerLocalM: {
        x: (start[0] + end[0]) / 2,
        y: isEntrance
          ? WHITE_BRIDGE_FOOD_HALL_FLOOR_Y + 2.8 + height / 2
          : (WHITE_BRIDGE_FOOD_HALL_FLOOR_Y +
            WHITE_BRIDGE_FOOD_HALL_CEILING_Y) / 2,
        z: (start[1] + end[1]) / 2,
      },
      sizeM: {
        x: Math.hypot(deltaX, deltaZ),
        y: height,
        z: 0.18,
      },
      rotationYRadians: -Math.atan2(deltaZ, deltaX),
    });
  }
  return colliders;
}

const LUKISKES_CELL_BAYS = Object.freeze([
  -9,
  -1.5,
  6,
  13.5,
  21,
  28.5,
  36,
  43.5,
]);
const LUKISKES_OPEN_CELL_INDICES = new Set([1, 2]);
const LUKISKES_GATE_BLENDER_Y = -31;
const LUKISKES_WALL_SEGMENTS = Object.freeze([
  ["east-south", [68, -48], [68, -36.5]],
  ["east-north", [68, -25.5], [68, 46]],
  ["north-east", [68, 46], [30, 52]],
  ["north", [30, 52], [-52, 46]],
  ["north-west", [-52, 46], [-80, 28]],
  ["west", [-80, 28], [-90, -10]],
  ["south-west", [-90, -10], [-74, -36]],
  ["south-corner", [-74, -36], [-38, -48]],
  ["south", [-38, -48], [68, -48]],
] as const);

function lukiskesWallCollider(
  name: string,
  start: readonly [number, number],
  end: readonly [number, number],
): AuthoredLandmarkBoxCollider {
  const deltaX = end[0] - start[0];
  const deltaBlenderY = end[1] - start[1];
  return {
    kind: "box",
    id: `security-wall-${name}`,
    centerLocalM: {
      x: (start[0] + end[0]) / 2,
      y: 2.6,
      z: -(start[1] + end[1]) / 2,
    },
    sizeM: {
      x: Math.hypot(deltaX, deltaBlenderY),
      y: 5.2,
      z: 0.55,
    },
    rotationYRadians: Math.atan2(deltaBlenderY, deltaX),
  };
}

function lukiskesCorridorWallColliders(
  side: -1 | 1,
): readonly AuthoredLandmarkBoxCollider[] {
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  let cursorBlenderY = -13;
  for (const [index, centreBlenderY] of LUKISKES_CELL_BAYS.entries()) {
    const openingStart = centreBlenderY - 0.72;
    const length = openingStart - cursorBlenderY;
    if (length > 0.02) {
      const centre = cursorBlenderY + length / 2;
      colliders.push({
        kind: "box",
        id: `cell-front-${side}-${index}`,
        centerLocalM: { x: side * 2.72, y: 2.4, z: -centre },
        sizeM: { x: 0.28, y: 4.8, z: length },
      });
    }
    cursorBlenderY = centreBlenderY + 0.72;
  }
  const length = 48 - cursorBlenderY;
  if (length > 0.02) {
    const centre = cursorBlenderY + length / 2;
    colliders.push({
      kind: "box",
      id: `cell-front-${side}-rear`,
      centerLocalM: { x: side * 2.72, y: 2.4, z: -centre },
      sizeM: { x: 0.28, y: 4.8, z: length },
    });
  }
  return colliders;
}

function lukiskesCellPartitionColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const boundaries = LUKISKES_CELL_BAYS.slice(0, -1).map(
    (centre, index) => (centre + LUKISKES_CELL_BAYS[index + 1]!) / 2,
  );
  return [-1, 1].flatMap((side) =>
    boundaries.map((centreBlenderY, index) => ({
      kind: "box" as const,
      id: `cell-partition-${side}-${index}`,
      centerLocalM: {
        x: side * 5.86,
        y: 2.4,
        z: -centreBlenderY,
      },
      sizeM: { x: 6.3, y: 4.8, z: 0.28 },
    }))
  );
}

/**
 * Walkable public nave, St Casimir Chapel, and crypt route for Vilnius Cathedral.
 *
 * The model-local X axis runs from the western portico towards the high altar
 * and Z runs across the three naves. The central western doorway, 3.2 m aisle,
 * and side aisles stay clear. Piers, grouped pew banks, altar steps, exterior
 * columns, and the enclosing shell share the visual model's dimensions. The
 * 0.62 m finish floor is reached by three sub-autostep-height portico steps.
 * One east-side portal opens into the domed St Casimir Chapel interpretation
 * and a physical stair continues to a compact Royal/Chapter crypt sequence.
 */
export const VILNIUS_CATHEDRAL_COLLISION: AuthoredLandmarkCollisionSpec =
  Object.freeze({
    landmarkId: "vilnius-cathedral",
    tileId: "2:1",
    buildingId: CATHEDRAL_BUILDING_ID,
    origin: Object.freeze({
      x: 634,
      y: 15,
      z: 267,
    }),
    rotationYRadians: 0,
    colliders: Object.freeze([
      {
        kind: "box" as const,
        id: "nave-floor",
        centerLocalM: { x: -2.5, y: 0.31, z: 0 },
        sizeM: { x: 49, y: 0.62, z: 54 },
      },
      {
        kind: "box" as const,
        id: "front-north-wall",
        centerLocalM: { x: -26.75, y: 6.7, z: -14.55 },
        sizeM: { x: 0.5, y: 13.4, z: 24.9 },
      },
      {
        kind: "box" as const,
        id: "front-south-wall",
        centerLocalM: { x: -26.75, y: 6.7, z: 14.55 },
        sizeM: { x: 0.5, y: 13.4, z: 24.9 },
      },
      {
        kind: "box" as const,
        id: "front-entrance-lintel",
        centerLocalM: { x: -26.75, y: 9.7, z: 0 },
        sizeM: { x: 0.5, y: 7.4, z: 4.2 },
      },
      {
        kind: "box" as const,
        id: "north-outer-wall",
        centerLocalM: { x: -2.5, y: 6.7, z: -26.75 },
        sizeM: { x: 49, y: 13.4, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "south-outer-wall",
        centerLocalM: { x: -2.5, y: 6.7, z: 26.75 },
        sizeM: { x: 49, y: 13.4, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "east-altar-wall-north",
        centerLocalM: { x: 21.75, y: 6.7, z: -4.6 },
        sizeM: { x: 0.5, y: 13.4, z: 44.8 },
      },
      {
        kind: "box" as const,
        id: "east-altar-wall-south",
        centerLocalM: { x: 21.75, y: 6.7, z: 25.4 },
        sizeM: { x: 0.5, y: 13.4, z: 3.2 },
      },
      {
        kind: "box" as const,
        id: "east-altar-wall-chapel-lintel",
        centerLocalM: { x: 21.75, y: 9.2, z: 20.8 },
        sizeM: { x: 0.5, y: 8.4, z: 6.0 },
      },
      {
        kind: "box" as const,
        id: "nave-ceiling",
        centerLocalM: { x: -2.5, y: 13.15, z: 0 },
        sizeM: { x: 49, y: 0.5, z: 54 },
      },
      {
        kind: "box" as const,
        id: "portico-step-0",
        centerLocalM: { x: -34.1, y: 0.1, z: 0 },
        sizeM: { x: 9, y: 0.2, z: 35.5 },
      },
      {
        kind: "box" as const,
        id: "portico-step-1",
        centerLocalM: { x: -33.65, y: 0.2, z: 0 },
        sizeM: { x: 8.1, y: 0.4, z: 34.5 },
      },
      {
        kind: "box" as const,
        id: "portico-step-2",
        centerLocalM: { x: -33.2, y: 0.3, z: 0 },
        sizeM: { x: 7.2, y: 0.6, z: 33.5 },
      },
      {
        kind: "box" as const,
        id: "portico-landing",
        centerLocalM: { x: -28.3, y: 0.3, z: 0 },
        sizeM: { x: 3.2, y: 0.6, z: 33.5 },
      },
      ...[-13.1, -7.85, -2.62, 2.62, 7.85, 13.1].map((z) => ({
        kind: "cylinder" as const,
        id: `portico-column-${z}`,
        centerLocalM: { x: -35, y: 6.025, z },
        heightM: 11.15,
        radiusM: 0.84,
      })),
      {
        kind: "box" as const,
        id: "portico-entablature",
        centerLocalM: { x: -31.5, y: 12.55, z: 0 },
        sizeM: { x: 9.2, y: 1.9, z: 32.8 },
      },
      ...[-19, -12, -5, 2, 9, 16].flatMap((x) =>
        [-9.2, 9.2].map((z) => ({
          kind: "box" as const,
          id: `nave-pier-${x}-${z}`,
          centerLocalM: { x, y: 6.2, z },
          sizeM: { x: 1.8, y: 11.2, z: 2 },
        }))
      ),
      ...[-4.7, 4.7].map((z) => ({
        kind: "box" as const,
        id: `pew-bank-${z}`,
        centerLocalM: { x: -3, y: 1.12, z },
        sizeM: { x: 27.2, y: 1, z: 6.2 },
      })),
      {
        kind: "box" as const,
        id: "altar-step-0",
        centerLocalM: { x: 18.9, y: 0.1, z: 0 },
        sizeM: { x: 6, y: 0.2, z: 18 },
      },
      {
        kind: "box" as const,
        id: "altar-step-1",
        centerLocalM: { x: 19.3, y: 0.2, z: 0 },
        sizeM: { x: 5.2, y: 0.4, z: 16.8 },
      },
      {
        kind: "box" as const,
        id: "altar-step-2",
        centerLocalM: { x: 19.7, y: 0.3, z: 0 },
        sizeM: { x: 4.4, y: 0.6, z: 15.6 },
      },
      {
        kind: "box" as const,
        id: "altar-table",
        centerLocalM: { x: 18.3, y: 1.25, z: 0 },
        sizeM: { x: 1.25, y: 1.3, z: 4.2 },
      },
      {
        kind: "box" as const,
        id: "casimir-threshold-step",
        centerLocalM: { x: 23.45, y: 0.225, z: 20.8 },
        sizeM: { x: 1.3, y: 0.45, z: 5.2 },
      },
      {
        kind: "box" as const,
        id: "casimir-floor-east",
        centerLocalM: { x: 31.3, y: 0.31, z: 20.8 },
        sizeM: { x: 8.6, y: 0.62, z: 11.8 },
      },
      {
        kind: "box" as const,
        id: "casimir-floor-west-north",
        centerLocalM: { x: 25.4, y: 0.31, z: 17.55 },
        sizeM: { x: 3.2, y: 0.62, z: 5.3 },
      },
      ...Array.from({ length: 12 }, (_, index) => index)
        .filter((index) => index !== 6)
        .map((index) => {
          const angle = index * Math.PI * 2 / 12;
          return {
            kind: "box" as const,
            id: `casimir-wall-${index}`,
            centerLocalM: {
              x: 29.7 + Math.cos(angle) * 6.76,
              y: 6.5,
              z: 20.8 - Math.sin(angle) * 6.76,
            },
            sizeM: {
              x: 0.55,
              y: 13.0,
              z: index === 5 || index === 7 ? 2.2 : 3.62,
            },
            rotationYRadians: angle,
          };
        }),
      {
        kind: "box" as const,
        id: "casimir-door-lintel",
        centerLocalM: { x: 22.94, y: 9.2, z: 20.8 },
        sizeM: { x: 0.55, y: 8.4, z: 3.8 },
        rotationYRadians: Math.PI,
      },
      {
        kind: "box" as const,
        id: "casimir-ceiling",
        centerLocalM: { x: 29.7, y: 12.55, z: 20.8 },
        sizeM: { x: 11.8, y: 0.5, z: 11.8 },
      },
      ...Array.from({ length: 12 }, (_, step) => {
        const height = (step + 1) * 0.25;
        return {
          kind: "box" as const,
          id: `crypt-step-${step}`,
          centerLocalM: {
            x: 25.6,
            y: -2.4 + height / 2,
            z: 26.2 - step * 0.5,
          },
          sizeM: { x: 2.8, y: height, z: 0.5 },
        };
      }),
      {
        kind: "box" as const,
        id: "crypt-floor",
        centerLocalM: { x: 29.7, y: -2.52, z: 13.3 },
        sizeM: { x: 11.8, y: 0.24, z: 29.0 },
      },
      {
        kind: "box" as const,
        id: "crypt-side-wall-35.6",
        centerLocalM: { x: 35.6, y: -1.3, z: 13.3 },
        sizeM: { x: 0.45, y: 2.4, z: 29.0 },
      },
      {
        kind: "box" as const,
        id: "crypt-side-wall-23.8",
        centerLocalM: { x: 23.8, y: -1.3, z: 13.3 },
        sizeM: { x: 0.45, y: 2.4, z: 29.0 },
      },
      {
        kind: "box" as const,
        id: "crypt-north-wall",
        centerLocalM: { x: 29.7, y: -1.3, z: -1.2 },
        sizeM: { x: 11.8, y: 2.4, z: 0.45 },
      },
      {
        kind: "box" as const,
        id: "crypt-south-wall",
        centerLocalM: { x: 29.7, y: -1.3, z: 27.8 },
        sizeM: { x: 11.8, y: 2.4, z: 0.45 },
      },
      {
        kind: "box" as const,
        id: "crypt-ceiling",
        centerLocalM: { x: 29.7, y: 1.1, z: 8.0 },
        sizeM: { x: 11.8, y: 0.35, z: 18.4 },
      },
      ...([16.3, 6.8] as const).map((z) => ({
        kind: "box" as const,
        id: `crypt-pier-${z}`,
        centerLocalM: { x: 29.7, y: -0.82, z },
        sizeM: { x: 1.1, y: 3.35, z: 1.1 },
      })),
    ]),
  });

function gediminasTowerColliders(): readonly AuthoredLandmarkCollider[] {
  const center = { x: -0.91, blenderY: -6.17 } as const;
  const rotation = 12.3 * Math.PI / 180;
  const radialBox = (
    id: string,
    angle: number,
    radius: number,
    width: number,
    centerY: number,
    height: number,
    tangentOffset = 0,
    radialThickness = 0.72,
  ): AuthoredLandmarkBoxCollider => {
    const tangentX = -Math.sin(angle);
    const tangentY = Math.cos(angle);
    const blenderX = center.x + tangentX * tangentOffset + Math.cos(angle) * radius;
    const blenderY = center.blenderY + tangentY * tangentOffset + Math.sin(angle) * radius;
    return {
      kind: "box",
      id,
      centerLocalM: { x: blenderX, y: centerY, z: -blenderY },
      sizeM: { x: radialThickness, y: height, z: width },
      rotationYRadians: angle,
    };
  };
  const shell = Array.from({ length: 8 }, (_, index) => {
    const angle = rotation + index * Math.PI / 4;
    if (index !== 5) {
      return [radialBox(`tower-wall-${index}`, angle, 7.42, 6.45, 10.2, 20.4)];
    }
    return [
      radialBox("tower-door-jamb-west", angle, 7.42, 2.35, 10.2, 20.4, -2.25),
      radialBox("tower-door-jamb-east", angle, 7.42, 2.35, 10.2, 20.4, 2.25),
      radialBox("tower-door-lintel", angle, 7.42, 2.15, 11.55, 17.7),
    ];
  }).flat();
  const floors = ([-1, 1] as const).flatMap((side) =>
    ([0.22, 5.48, 10.86, 16.18] as const).flatMap((floorY, level) => [
      {
        kind: "box" as const,
        id: `tower-floor-x-${level}-${side}`,
        centerLocalM: { x: center.x + side * 5.2, y: floorY, z: -center.blenderY },
        sizeM: { x: 2.4, y: 0.34, z: 13 },
      },
      {
        kind: "box" as const,
        id: `tower-floor-z-${level}-${side}`,
        centerLocalM: { x: center.x, y: floorY, z: -(center.blenderY + side * 5.2) },
        sizeM: { x: 8, y: 0.34, z: 2.4 },
      },
    ])
  );
  const stairs = ([0.3, 5.56, 10.94, 16.26] as const).flatMap(
    (baseY, level) => Array.from({ length: 30 }, (_, step) => {
      const angle = rotation + step * Math.PI * 2 / 30;
      const topY = baseY + (step + 1) * (5.18 / 30);
      const blenderX = center.x + Math.cos(angle) * 3.45;
      const blenderY = center.blenderY + Math.sin(angle) * 3.45;
      return {
        kind: "box" as const,
        id: `tower-stair-${level}-${step}`,
        centerLocalM: { x: blenderX, y: topY - 0.08, z: -blenderY },
        sizeM: { x: 2.2, y: 0.16, z: 0.62 },
        rotationYRadians: angle,
      };
    })
  );
  const entrySteps = Array.from({ length: 7 }, (_, step) => {
    const topY = -1.15 + step * 0.25;
    const bottomY = -1.4;
    const height = topY - bottomY;
    return radialBox(
      `tower-entry-step-${step}`,
      rotation + 5 * Math.PI / 4,
      10.2 - step * 0.5,
      3.2,
      bottomY + height / 2,
      height,
    );
  });
  return [
    ...shell,
    ...floors,
    radialBox("tower-entry-landing", rotation, 4.5, 2.2, 0.22, 0.34, 0, 3.6),
    ...([-1, 1] as const).flatMap((side) => [
      {
        kind: "box" as const,
        id: `tower-roof-deck-x-${side}`,
        centerLocalM: { x: center.x + side * 5.2, y: 21.74, z: -center.blenderY },
        sizeM: { x: 2.4, y: 0.36, z: 13 },
      },
      {
        kind: "box" as const,
        id: `tower-roof-deck-z-${side}`,
        centerLocalM: { x: center.x, y: 21.74, z: -(center.blenderY + side * 5.2) },
        sizeM: { x: 8, y: 0.36, z: 2.4 },
      },
    ]),
    {
      kind: "cylinder",
      id: "tower-stair-core",
      centerLocalM: { x: center.x, y: 10.6, z: -center.blenderY },
      heightM: 21.2,
      radiusM: 1.05,
    },
    ...entrySteps,
    ...stairs,
  ];
}

/** Four museum floors and the public observation deck in Gediminas Tower. */
export const VILNIUS_GEDIMINAS_CASTLE_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "gediminas-castle",
    tileId: "3:0",
    buildingId: GEDIMINAS_CASTLE_TOWER_BUILDING_ID,
    origin: Object.freeze({ x: 820.637, y: 38.98, z: 160.529 }),
    rotationYRadians: 0,
    colliders: Object.freeze(gediminasTowerColliders()),
  });

function grandDukesColliders(): readonly AuthoredLandmarkCollider[] {
  const result: AuthoredLandmarkCollider[] = [
    { kind: "box", id: "floor-west", centerLocalM: { x: -38, y: 0.15, z: 45.5 }, sizeM: { x: 48, y: 0.3, z: 19 } },
    { kind: "box", id: "floor-east", centerLocalM: { x: 26, y: 0.15, z: 45.5 }, sizeM: { x: 72, y: 0.3, z: 19 } },
    { kind: "box", id: "floor-stair-front", centerLocalM: { x: -12, y: 0.15, z: 52 }, sizeM: { x: 4, y: 0.3, z: 6 } },
    { kind: "box", id: "floor-stair-rear", centerLocalM: { x: -12, y: 0.15, z: 38.5 }, sizeM: { x: 4, y: 0.3, z: 5 } },
    { kind: "box", id: "upper-floor-west", centerLocalM: { x: -27, y: 6.25, z: 45.5 }, sizeM: { x: 70, y: 0.3, z: 19 } },
    { kind: "box", id: "upper-floor-east", centerLocalM: { x: 37, y: 6.25, z: 45.5 }, sizeM: { x: 50, y: 0.3, z: 19 } },
    { kind: "box", id: "upper-stair-front", centerLocalM: { x: 10, y: 6.25, z: 52.5 }, sizeM: { x: 4, y: 0.3, z: 5 } },
    { kind: "box", id: "upper-stair-rear", centerLocalM: { x: 10, y: 6.25, z: 38.5 }, sizeM: { x: 4, y: 0.3, z: 5 } },
    ...Array.from({ length: 5 }, (_, step) => {
      const topY = -0.74 + step * 0.26;
      const height = topY + 1;
      return {
        kind: "box" as const,
        id: `entry-step-${step}`,
        centerLocalM: { x: 0, y: -1 + height / 2, z: 59 - step * 0.9 },
        sizeM: { x: 4, y: height, z: 1 },
      };
    }),
    { kind: "box", id: "roof", centerLocalM: { x: 0, y: 20, z: 45.5 }, sizeM: { x: 124, y: 0.55, z: 19 } },
    { kind: "box", id: "west-end", centerLocalM: { x: -62, y: 10, z: 45.5 }, sizeM: { x: 0.6, y: 20, z: 19 } },
    { kind: "box", id: "east-end", centerLocalM: { x: 62, y: 10, z: 45.5 }, sizeM: { x: 0.6, y: 20, z: 19 } },
    { kind: "box", id: "south-west-wall", centerLocalM: { x: -32.5, y: 10, z: 55 }, sizeM: { x: 59, y: 20, z: 0.6 } },
    { kind: "box", id: "south-east-wall", centerLocalM: { x: 32.5, y: 10, z: 55 }, sizeM: { x: 59, y: 20, z: 0.6 } },
    { kind: "box", id: "south-lintel", centerLocalM: { x: 0, y: 11.6, z: 55 }, sizeM: { x: 6, y: 16.8, z: 0.6 } },
    { kind: "box", id: "court-west-wall", centerLocalM: { x: -32.5, y: 10, z: 36 }, sizeM: { x: 59, y: 20, z: 0.6 } },
    { kind: "box", id: "court-east-wall", centerLocalM: { x: 32.5, y: 10, z: 36 }, sizeM: { x: 59, y: 20, z: 0.6 } },
    { kind: "box", id: "court-lintel", centerLocalM: { x: 0, y: 11.6, z: 36 }, sizeM: { x: 6, y: 16.8, z: 0.6 } },
    { kind: "box", id: "archaeology-floor", centerLocalM: { x: -8, y: -3.28, z: 45.5 }, sizeM: { x: 80, y: 0.28, z: 17.5 } },
    { kind: "box", id: "archaeology-south", centerLocalM: { x: -8, y: -1.58, z: 54.25 }, sizeM: { x: 80, y: 3.4, z: 0.5 } },
    { kind: "box", id: "archaeology-north", centerLocalM: { x: -8, y: -1.58, z: 36.75 }, sizeM: { x: 80, y: 3.4, z: 0.5 } },
    { kind: "box", id: "archaeology-west", centerLocalM: { x: -48, y: -1.58, z: 45.5 }, sizeM: { x: 0.5, y: 3.4, z: 17.5 } },
    { kind: "box", id: "archaeology-east", centerLocalM: { x: 32, y: -1.58, z: 45.5 }, sizeM: { x: 0.5, y: 3.4, z: 17.5 } },
    { kind: "box", id: "rear-range", centerLocalM: { x: 0, y: 10, z: -45.5 }, sizeM: { x: 124, y: 20, z: 19 } },
    { kind: "box", id: "west-range", centerLocalM: { x: -52.5, y: 10, z: 0 }, sizeM: { x: 19, y: 20, z: 72 } },
    { kind: "box", id: "east-range", centerLocalM: { x: 52.5, y: 10, z: 0 }, sizeM: { x: 19, y: 20, z: 72 } },
  ];
  for (let step = 0; step < 12; step += 1) {
    const height = (step + 1) * 0.25;
    result.push({ kind: "box", id: `archaeology-step-${step}`, centerLocalM: { x: -12, y: -3.15 + height / 2, z: 48.75 - step * 0.5 }, sizeM: { x: 3.5, y: height, z: 0.5 } });
  }
  for (let step = 0; step < 25; step += 1) {
    const height = (step + 1) * 0.248;
    result.push({ kind: "box", id: `state-step-${step}`, centerLocalM: { x: 10, y: height / 2, z: 49.9 - step * 0.32 }, sizeM: { x: 3.5, y: height, z: 0.32 } });
  }
  for (const [level, baseY, height] of [["ground", 0.3, 5.95], ["state", 6.4, 6.3]] as const) {
    for (const x of [-28, 0, 28]) {
      for (const [side, z] of [["south", 51.25], ["north", 39.75]] as const) {
        const entranceSegment = x === 0 && side === "south";
        result.push({ kind: "box", id: `${level}-partition-${x}-${side}`, centerLocalM: { x, y: baseY + height / 2, z: entranceSegment ? 49.75 : z }, sizeM: { x: 0.42, y: height, z: entranceSegment ? 4.5 : 7.5 } });
      }
    }
  }
  return result;
}

export const VILNIUS_GRAND_DUKES_PALACE_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "palace-grand-dukes",
    tileId: "2:0",
    buildingId: GRAND_DUKES_PALACE_BUILDING_ID,
    origin: Object.freeze({ x: 720.396, y: 16.532, z: 224.592 }),
    rotationYRadians: 0.348004,
    colliders: Object.freeze(grandDukesColliders()),
  });

function gateOfDawnColliders(): readonly AuthoredLandmarkCollider[] {
  const colliders: AuthoredLandmarkCollider[] = [
    { kind: "box", id: "east-pier", centerLocalM: { x: 5.55, y: 4, z: 0 }, sizeM: { x: 4.9, y: 8, z: 20 } },
    { kind: "box", id: "west-outer-wall", centerLocalM: { x: -8, y: 4, z: 0 }, sizeM: { x: 0.45, y: 8, z: 20 } },
    { kind: "box", id: "west-passage-wall", centerLocalM: { x: -3.1, y: 4, z: 0 }, sizeM: { x: 0.45, y: 8, z: 20 } },
    { kind: "box", id: "west-north-wall", centerLocalM: { x: -5.55, y: 4, z: -10 }, sizeM: { x: 4.9, y: 8, z: 0.45 } },
    { kind: "box", id: "west-south-left", centerLocalM: { x: -7.38, y: 4, z: 10 }, sizeM: { x: 1.25, y: 8, z: 0.45 } },
    { kind: "box", id: "west-south-right", centerLocalM: { x: -3.72, y: 4, z: 10 }, sizeM: { x: 1.25, y: 8, z: 0.45 } },
    { kind: "box", id: "west-south-lintel", centerLocalM: { x: -5.55, y: 5.2, z: 10 }, sizeM: { x: 2.4, y: 5.6, z: 0.45 } },
    { kind: "box", id: "chapel-floor-east", centerLocalM: { x: 2.1, y: 8.15, z: 0 }, sizeM: { x: 11.8, y: 0.3, z: 20 } },
    { kind: "box", id: "chapel-floor-west", centerLocalM: { x: -7.65, y: 8.15, z: 0 }, sizeM: { x: 0.7, y: 0.3, z: 20 } },
    { kind: "box", id: "chapel-floor-south", centerLocalM: { x: -5.55, y: 8.15, z: 9 }, sizeM: { x: 3.5, y: 0.3, z: 2 } },
    { kind: "box", id: "chapel-floor-landing", centerLocalM: { x: -5.55, y: 8.15, z: -8.91 }, sizeM: { x: 3.5, y: 0.3, z: 2.18 } },
    { kind: "box", id: "chapel-west", centerLocalM: { x: -8, y: 11.9, z: 0 }, sizeM: { x: 0.45, y: 7.8, z: 20 } },
    { kind: "box", id: "chapel-east", centerLocalM: { x: 8, y: 11.9, z: 0 }, sizeM: { x: 0.45, y: 7.8, z: 20 } },
    { kind: "box", id: "chapel-north", centerLocalM: { x: 0, y: 11.9, z: -10 }, sizeM: { x: 16, y: 7.8, z: 0.45 } },
    { kind: "box", id: "chapel-south", centerLocalM: { x: 0, y: 11.9, z: 10 }, sizeM: { x: 16, y: 7.8, z: 0.45 } },
    { kind: "box", id: "chapel-roof", centerLocalM: { x: 0, y: 15.75, z: 0 }, sizeM: { x: 16, y: 0.4, z: 20 } },
  ];
  const stairCount = 44;
  const stairTreadM = 16 / stairCount;
  for (let step = 0; step < stairCount; step += 1) {
    const height = (step + 1) * (8.1 / stairCount);
    colliders.push({ kind: "box", id: `stair-${step}`, centerLocalM: { x: -5.55, y: height / 2, z: 8 - step * stairTreadM }, sizeM: { x: 3.5, y: height, z: stairTreadM } });
  }
  return colliders;
}

export const VILNIUS_GATE_OF_DAWN_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "gate-of-dawn",
    // The official passage footprint is owned by 3:5 even though the chapel
    // asset origin lies immediately south in 3:6.
    tileId: "3:5",
    buildingId: GATE_OF_DAWN_BUILDING_ID,
    origin: Object.freeze({ x: 768.81, y: 50.138, z: 1549.9 }),
    rotationYRadians: 0.167989,
    colliders: Object.freeze(gateOfDawnColliders()),
  });

const UNIVERSITY_LIBRARY_ANGLE = -49.5 * Math.PI / 180;

function universityLibraryPoint(
  longitudinal: number,
  lateral: number,
  y: number,
): Readonly<{ x: number; y: number; z: number }> {
  const tangentX = Math.cos(UNIVERSITY_LIBRARY_ANGLE);
  const tangentY = Math.sin(UNIVERSITY_LIBRARY_ANGLE);
  const normalX = -tangentY;
  const normalY = tangentX;
  const blenderX = -49.6 + tangentX * longitudinal + normalX * lateral;
  const blenderY = -28.8 + tangentY * longitudinal + normalY * lateral;
  return { x: blenderX, y, z: -blenderY };
}

function universityLibraryColliders(): readonly AuthoredLandmarkCollider[] {
  const boxAt = (
    id: string,
    longitudinal: number,
    lateral: number,
    y: number,
    sizeX: number,
    sizeY: number,
    sizeZ: number,
  ): AuthoredLandmarkBoxCollider => ({
    kind: "box",
    id,
    centerLocalM: universityLibraryPoint(longitudinal, lateral, y),
    sizeM: { x: sizeX, y: sizeY, z: sizeZ },
    rotationYRadians: UNIVERSITY_LIBRARY_ANGLE,
  });
  const colliders: AuthoredLandmarkCollider[] = [
    boxAt("ground-floor", 0, 0, 0.14, 58, 0.28, 14),
    boxAt("southwest-wall", 0, -7, 5.9, 58, 11.8, 0.45),
    boxAt("court-wall-west", -15.5, 7, 5.9, 27, 11.8, 0.45),
    boxAt("court-wall-east", 15.5, 7, 5.9, 27, 11.8, 0.45),
    boxAt("court-lintel", 0, 7, 7.3, 4, 9, 0.45),
    boxAt("west-end", -29, 0, 5.9, 0.45, 11.8, 14),
    boxAt("east-end", 29, 0, 5.9, 0.45, 11.8, 14),
    boxAt("upper-floor-west", -18.5, 0, 5.12, 21, 0.28, 14),
    boxAt("upper-floor-east", 14.5, 0, 5.12, 29, 0.28, 14),
    boxAt("upper-floor-stair-left", -4, -2.5, 5.12, 8, 0.28, 9),
    boxAt("upper-floor-stair-right", -4, 6, 5.12, 8, 0.28, 2),
    boxAt("ceiling", 0, 0, 11.8, 58, 0.38, 14),
  ];
  const entryBottomM = -1.65;
  const entryStepCount = 10;
  const entryRiseM = (0.28 - entryBottomM) / entryStepCount;
  for (let step = 0; step < entryStepCount; step += 1) {
    const height = (step + 1) * entryRiseM;
    colliders.push(boxAt(
      `entry-step-${step}`,
      0,
      9.85 - step * 0.3,
      entryBottomM + height / 2,
      3.6,
      height,
      0.3,
    ));
  }
  for (let step = 0; step < 21; step += 1) {
    const height = (step + 1) * (5 / 21);
    colliders.push(boxAt(`stair-${step}`, -8 + step * 0.38, 3.55, height / 2, 0.38, height, 3.2));
  }
  for (const [level, baseY, height] of [["ground", 0.28, 4.52], ["upper", 5.26, 6.54]] as const) {
    for (const longitudinal of [-14.5, 5, 18]) {
      for (const side of [-1, 1] as const) {
        colliders.push(boxAt(`${level}-partition-${longitudinal}-${side}`, longitudinal, side * 4.4, baseY + height / 2, 0.42, height, 5.2));
      }
    }
  }
  return colliders;
}

export const VILNIUS_UNIVERSITY_LIBRARY_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "vilnius-university-main-ensemble",
    tileId: "2:2",
    buildingId: VILNIUS_UNIVERSITY_LIBRARY_BUILDING_ID,
    origin: Object.freeze({ x: 633.185, y: 20.148, z: 621.392 }),
    rotationYRadians: -2.241756,
    colliders: Object.freeze(universityLibraryColliders()),
  });

/**
 * Public-route collision for Lukiškės Prison.
 *
 * The checked-in GRPK complex is split across three large overlapping source
 * footprints. The main footprint receives this authored compound collision;
 * the two adjacent source masses are suppressed below. Model-local Z is the
 * inverse of Blender Y. The public gate faces Lukiškių skersgatvis on local
 * positive X, safely inside the real road and sidewalk. From there, the route
 * passes the administration ranges, the church hub, and one complete lower
 * cell wing. Four cells (two per side) are open and furnished; the remaining
 * barred doors correctly stop movement.
 */
export const VILNIUS_LUKISKES_PRISON_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "lukiskes-prison",
    tileId: "-3:-2",
    buildingId: LUKISKES_PRISON_MAIN_BUILDING_ID,
    origin: Object.freeze({
      x: -734.564,
      y: 16.551,
      z: -336.445,
    }),
    rotationYRadians: 1.113302,
    colliders: Object.freeze([
      {
        kind: "box" as const,
        id: "admin-south",
        centerLocalM: { x: 52, y: 8, z: 41 },
        sizeM: { x: 20, y: 16, z: 14 },
      },
      {
        kind: "box" as const,
        id: "admin-north",
        centerLocalM: { x: 52, y: 8, z: 6.5 },
        sizeM: { x: 20, y: 16, z: 43 },
      },
      {
        kind: "box" as const,
        id: "entry-floor",
        centerLocalM: { x: 37, y: 0.12, z: -LUKISKES_GATE_BLENDER_Y },
        sizeM: { x: 62, y: 0.24, z: 6 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `connector-wall-${side}`,
        centerLocalM: {
          x: 37,
          y: 2.4,
          z: -(LUKISKES_GATE_BLENDER_Y + side * 3),
        },
        sizeM: { x: 62, y: 4.8, z: 0.35 },
      })),
      {
        kind: "box" as const,
        id: "hub-floor",
        centerLocalM: { x: 0, y: 0.12, z: 19 },
        sizeM: { x: 17, y: 0.24, z: 17 },
      },
      {
        kind: "box" as const,
        id: "cell-floor",
        centerLocalM: { x: 0, y: 0.12, z: -17.5 },
        sizeM: { x: 18.6, y: 0.24, z: 62 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `cell-outer-${side}`,
        centerLocalM: { x: side * 9.05, y: 5.25, z: -17.5 },
        sizeM: { x: 0.55, y: 10.5, z: 62 },
      })),
      {
        kind: "box" as const,
        id: "cell-rear",
        centerLocalM: { x: 0, y: 5.25, z: -48.25 },
        sizeM: { x: 18.6, y: 10.5, z: 0.55 },
      },
      {
        kind: "box" as const,
        id: "cell-ceiling",
        centerLocalM: { x: 0, y: 10.28, z: -17.5 },
        sizeM: { x: 18.6, y: 0.42, z: 62 },
      },
      ...lukiskesCorridorWallColliders(-1),
      ...lukiskesCorridorWallColliders(1),
      ...lukiskesCellPartitionColliders(),
      ...[-1, 1].flatMap((side) =>
        LUKISKES_CELL_BAYS.flatMap((centreBlenderY, index) =>
          LUKISKES_OPEN_CELL_INDICES.has(index)
            ? [
              {
                kind: "box" as const,
                id: `cell-bed-${side}-${index}`,
                centerLocalM: {
                  x: side * 6.55,
                  y: 0.41,
                  z: -(centreBlenderY + 1.25),
                },
                sizeM: { x: 1.75, y: 0.82, z: 2.45 },
              },
              {
                kind: "cylinder" as const,
                id: `cell-toilet-${side}-${index}`,
                centerLocalM: {
                  x: side * 7.65,
                  y: 0.35,
                  z: -(centreBlenderY - 2.35),
                },
                heightM: 0.7,
                radiusM: 0.38,
              },
            ]
            : [{
              kind: "box" as const,
              id: `closed-cell-door-${side}-${index}`,
              centerLocalM: {
                x: side * 2.68,
                y: 1.85,
                z: -centreBlenderY,
              },
              sizeM: { x: 0.18, y: 3.7, z: 1.28 },
            }]
        )
      ),
      {
        kind: "box" as const,
        id: "west-radial-wing",
        centerLocalM: { x: -30, y: 8.5, z: 1.5 },
        sizeM: { x: 62, y: 17, z: 17 },
        rotationYRadians: Math.PI / 6,
      },
      {
        kind: "box" as const,
        id: "east-radial-wing",
        centerLocalM: { x: 30, y: 8.5, z: 1.5 },
        sizeM: { x: 62, y: 17, z: 17 },
        rotationYRadians: Math.PI * 5 / 6,
      },
      {
        kind: "box" as const,
        id: "hospital",
        centerLocalM: { x: -66, y: 6.25, z: -1 },
        sizeM: { x: 27, y: 12.5, z: 35 },
      },
      {
        kind: "box" as const,
        id: "service-range",
        centerLocalM: { x: -48, y: 5.25, z: -35 },
        sizeM: { x: 27, y: 10.5, z: 17 },
      },
      ...LUKISKES_WALL_SEGMENTS.map(([name, start, end]) =>
        lukiskesWallCollider(name, start, end)
      ),
    ]),
  });

export const VILNIUS_LUKISKES_PRISON_FOOTPRINTS = Object.freeze([
  Object.freeze({
    tileId: "-3:-2" as const,
    buildingId: LUKISKES_PRISON_MAIN_BUILDING_ID,
  }),
  Object.freeze({
    tileId: "-3:-2" as const,
    buildingId: LUKISKES_PRISON_EAST_BUILDING_ID,
  }),
  Object.freeze({
    tileId: "-4:-2" as const,
    buildingId: LUKISKES_PRISON_WEST_BUILDING_ID,
  }),
]);

const LUKISKES_PRISON_SUPPRESSED_COLLISIONS:
  readonly AuthoredLandmarkCollisionSpec[] = Object.freeze(
    VILNIUS_LUKISKES_PRISON_FOOTPRINTS.slice(1).map((footprint) =>
      Object.freeze({
        landmarkId: "lukiskes-prison",
        tileId: footprint.tileId,
        buildingId: footprint.buildingId,
        origin: VILNIUS_LUKISKES_PRISON_COLLISION.origin,
        rotationYRadians:
          VILNIUS_LUKISKES_PRISON_COLLISION.rotationYRadians,
        colliders: Object.freeze([]),
      })
    ),
  );

function presidentialPalaceEdgeCollider(
  id: string,
  start: readonly [number, number],
  end: readonly [number, number],
  centerY = 7.29,
  height = 14.58,
  thickness = 0.58,
): AuthoredLandmarkBoxCollider {
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  return {
    kind: "box",
    id,
    centerLocalM: {
      x: (start[0] + end[0]) / 2,
      y: centerY,
      z: (start[1] + end[1]) / 2,
    },
    sizeM: {
      x: Math.hypot(deltaX, deltaZ),
      y: height,
      z: thickness,
    },
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
  };
}

function presidentialPalaceSplitEdge(
  start: readonly [number, number],
  end: readonly [number, number],
  gapWidthM: number,
): readonly [readonly [number, number], readonly [number, number]] {
  const length = Math.hypot(end[0] - start[0], end[1] - start[1]);
  const halfFraction = gapWidthM / length / 2;
  const at = (progress: number): readonly [number, number] => [
    start[0] + (end[0] - start[0]) * progress,
    start[1] + (end[1] - start[1]) * progress,
  ];
  return [at(0.5 - halfFraction), at(0.5 + halfFraction)];
}

function presidentialPalaceExteriorColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  const entries = new Map<number, Readonly<{ id: string; gapWidthM: number }>>([
    [6, { id: "courtyard-entry", gapWidthM: 4.2 }],
    // The archived Presidency tour places the public visitor entrance in the
    // left wing of the Daukanto Square facade.
    [20, { id: "daukanto-entry", gapWidthM: 3.6 }],
  ]);
  for (const [index, start] of PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.entries()) {
    const end = PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M[
      (index + 1) % PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.length
    ]!;
    const entry = entries.get(index);
    if (entry === undefined) {
      colliders.push(
        presidentialPalaceEdgeCollider(`outer-wall-${index}`, start, end),
      );
      continue;
    }
    const [first, second] = presidentialPalaceSplitEdge(
      start,
      end,
      entry.gapWidthM,
    );
    colliders.push(
      presidentialPalaceEdgeCollider(`${entry.id}-south`, start, first),
      presidentialPalaceEdgeCollider(`${entry.id}-north`, second, end),
      presidentialPalaceEdgeCollider(
        `${entry.id}-lintel`,
        first,
        second,
        9.225,
        9.35,
      ),
    );
  }
  return colliders;
}

function presidentialPalaceFacadePoint(
  edgeIndex: number,
  fraction: number,
  outsetM: number,
): Readonly<{
  x: number;
  z: number;
  rotationYRadians: number;
  lengthM: number;
}> {
  const start = PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M[edgeIndex]!;
  const end = PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M[
    (edgeIndex + 1) % PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.length
  ]!;
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  const lengthM = Math.hypot(deltaX, deltaZ);
  const tangentX = deltaX / lengthM;
  const tangentZ = deltaZ / lengthM;
  const inwardX = -tangentZ;
  const inwardZ = tangentX;
  return {
    x: start[0] + deltaX * fraction - inwardX * outsetM,
    z: start[1] + deltaZ * fraction - inwardZ * outsetM,
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
    lengthM,
  };
}

function presidentialPalacePorticoColliders(): readonly AuthoredLandmarkCollider[] {
  const colliders: AuthoredLandmarkCollider[] = [];
  for (const [porticoIndex, edgeIndex] of [18, 14].entries()) {
    const center = presidentialPalaceFacadePoint(edgeIndex, 0.5, 0.95);
    const spanM = Math.min(center.lengthM - 0.55, 13.35);
    colliders.push(
      {
        kind: "box",
        id: `daukanto-portico-platform-${porticoIndex}`,
        centerLocalM: { x: center.x, y: 0.64, z: center.z },
        sizeM: { x: spanM + 0.30, y: 0.16, z: 1.95 },
        rotationYRadians: center.rotationYRadians,
      },
    );
    for (const [stepIndex, [outsetM, heightM]] of ([
      [1.94, 0.46],
      [2.27, 0.22],
    ] as const).entries()) {
      const step = presidentialPalaceFacadePoint(edgeIndex, 0.5, outsetM);
      colliders.push({
        kind: "box",
        id: `daukanto-portico-${porticoIndex}-step-${stepIndex}`,
        centerLocalM: { x: step.x, y: heightM / 2, z: step.z },
        sizeM: { x: spanM + 0.55, y: heightM, z: 0.46 },
        rotationYRadians: step.rotationYRadians,
      });
    }
    for (const [columnIndex, fraction] of [0.10, 0.3667, 0.6333, 0.90].entries()) {
      const column = presidentialPalaceFacadePoint(edgeIndex, fraction, 1.65);
      colliders.push({
        kind: "cylinder",
        id: `daukanto-portico-${porticoIndex}-column-${columnIndex}`,
        centerLocalM: { x: column.x, y: 4.05, z: column.z },
        heightM: 5.84,
        radiusM: 0.36,
      });
    }
  }
  return colliders;
}

const PRESIDENTIAL_PALACE_GROUND_PLATES = Object.freeze([
  ["service-vestibule", 6.0, -42.0, 5.0, 5.0],
  ["service-south-threshold", 3.0, -39.0, 4.0, 1.4],
  ["south-gallery", -1.0, -36.0, 14.0, 5.0],
  ["south-delegations-threshold", -4.0, -32.25, 3.0, 2.8],
  ["delegations-lounge", -9.0, -28.0, 17.0, 6.0],
  ["delegations-press-threshold", -7.0, -23.5, 3.0, 3.2],
  ["press-briefing", -5.2, -18.0, 15.0, 8.0],
  ["press-stair-threshold", -4.0, -11.0, 3.0, 6.2],
  ["courtyard-entry-throat", 9.5, -2.4, 3.5, 4.0],
  ["grand-stair-hall", -1.0, -4.0, 18.0, 8.0],
  ["stair-dining-threshold", 0.0, 2.5, 3.0, 5.2],
  ["state-dining", 1.5, 9.0, 18.0, 8.0],
  ["dining-maps-threshold", 2.0, 14.5, 3.0, 3.2],
  ["maps-room", 3.0, 19.5, 15.0, 7.0],
  ["maps-reception-threshold", 7.0, 25.0, 3.0, 4.2],
  ["north-reception", 9.0, 31.5, 23.0, 9.0],
] as const);

const PRESIDENTIAL_PALACE_STAIRWELL_LOCAL_M = Object.freeze({
  minimumX: -1.25,
  maximumX: 7.25,
  minimumZ: -4.85,
  maximumZ: 0.15,
});

type PresidentialPalaceInterval = readonly [minimum: number, maximum: number];

function presidentialPalaceHorizontalIntervals(
  z: number,
): readonly PresidentialPalaceInterval[] {
  const intersections: number[] = [];
  for (const [index, start] of PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.entries()) {
    const end = PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M[
      (index + 1) % PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.length
    ]!;
    const minimumZ = Math.min(start[1], end[1]);
    const maximumZ = Math.max(start[1], end[1]);
    if (z < minimumZ || z >= maximumZ || start[1] === end[1]) {
      continue;
    }
    const progress = (z - start[1]) / (end[1] - start[1]);
    intersections.push(start[0] + (end[0] - start[0]) * progress);
  }
  intersections.sort((left, right) => left - right);
  const intervals: PresidentialPalaceInterval[] = [];
  for (let index = 0; index + 1 < intersections.length; index += 2) {
    intervals.push([intersections[index]!, intersections[index + 1]!]);
  }
  return intervals;
}

function presidentialPalaceIntersectIntervals(
  left: readonly PresidentialPalaceInterval[],
  right: readonly PresidentialPalaceInterval[],
): readonly PresidentialPalaceInterval[] {
  const intersections: PresidentialPalaceInterval[] = [];
  for (const leftInterval of left) {
    for (const rightInterval of right) {
      const minimum = Math.max(leftInterval[0], rightInterval[0]);
      const maximum = Math.min(leftInterval[1], rightInterval[1]);
      if (maximum - minimum > 0.05) {
        intersections.push([minimum, maximum]);
      }
    }
  }
  return intersections;
}

/**
 * Continuous upper-storey support follows the official concave outline.
 * Narrow longitudinal strips approximate its angled edges without extending a
 * single broad collider over Daukanto Square or Universiteto Street. The only
 * deliberate opening is the exact grand-stair well.
 */
function presidentialPalaceUpperFloorColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const minimumZ = Math.min(
    ...PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.map((point) => point[1]),
  ) + 0.31;
  const maximumZ = Math.max(
    ...PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.map((point) => point[1]),
  ) - 0.31;
  const bandDepthM = 1.4;
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  const bandBoundaries = [
    minimumZ,
    maximumZ,
    PRESIDENTIAL_PALACE_STAIRWELL_LOCAL_M.minimumZ,
    PRESIDENTIAL_PALACE_STAIRWELL_LOCAL_M.maximumZ,
  ];
  for (let z = minimumZ + bandDepthM; z < maximumZ; z += bandDepthM) {
    bandBoundaries.push(z);
  }
  bandBoundaries.sort((left, right) => left - right);
  const uniqueBoundaries = bandBoundaries.filter(
    (boundary, index) =>
      boundary >= minimumZ &&
      boundary <= maximumZ &&
      (index === 0 || Math.abs(boundary - bandBoundaries[index - 1]!) > 0.001),
  );
  for (let bandIndex = 0; bandIndex + 1 < uniqueBoundaries.length; bandIndex += 1) {
    const bandStartZ = uniqueBoundaries[bandIndex]!;
    const bandEndZ = uniqueBoundaries[bandIndex + 1]!;
    const samples = [
      bandStartZ + 0.001,
      (bandStartZ + bandEndZ) / 2,
      bandEndZ - 0.001,
    ];
    let intervals = presidentialPalaceHorizontalIntervals(samples[0]!);
    for (const sample of samples.slice(1)) {
      intervals = presidentialPalaceIntersectIntervals(
        intervals,
        presidentialPalaceHorizontalIntervals(sample),
      );
    }
    const overlapsStairwell =
      bandEndZ > PRESIDENTIAL_PALACE_STAIRWELL_LOCAL_M.minimumZ &&
      bandStartZ < PRESIDENTIAL_PALACE_STAIRWELL_LOCAL_M.maximumZ;
    let segmentIndex = 0;
    for (const [rawMinimumX, rawMaximumX] of intervals) {
      const minimumX = rawMinimumX + 0.31;
      const maximumX = rawMaximumX - 0.31;
      const segments: PresidentialPalaceInterval[] = overlapsStairwell
        ? [
            [
              minimumX,
              Math.min(
                maximumX,
                PRESIDENTIAL_PALACE_STAIRWELL_LOCAL_M.minimumX,
              ),
            ],
            [
              Math.max(
                minimumX,
                PRESIDENTIAL_PALACE_STAIRWELL_LOCAL_M.maximumX,
              ),
              maximumX,
            ],
          ]
        : [[minimumX, maximumX]];
      for (const [segmentMinimumX, segmentMaximumX] of segments) {
        if (segmentMaximumX - segmentMinimumX <= 0.05) {
          continue;
        }
        colliders.push({
          kind: "box",
          id: `upper-floor-strip-${bandIndex}-${segmentIndex}`,
          centerLocalM: {
            x: (segmentMinimumX + segmentMaximumX) / 2,
            y: 7.12,
            z: (bandStartZ + bandEndZ) / 2,
          },
          sizeM: {
            x: segmentMaximumX - segmentMinimumX,
            y: 0.20,
            z: bandEndZ - bandStartZ + 0.02,
          },
        });
        segmentIndex += 1;
      }
    }
  }
  return colliders;
}

const PRESIDENTIAL_PALACE_UPPER_FLOOR_COLLIDERS = Object.freeze(
  presidentialPalaceUpperFloorColliders(),
);

function presidentialPalaceFloorColliders(): readonly AuthoredLandmarkBoxCollider[] {
  return [
    ...PRESIDENTIAL_PALACE_GROUND_PLATES.map(
      ([id, x, z, width, depth]) => ({
        kind: "box" as const,
        id: `ground-floor-${id}`,
        centerLocalM: { x, y: 0.66, z },
        sizeM: { x: width, y: 0.12, z: depth },
      }),
    ),
    ...PRESIDENTIAL_PALACE_UPPER_FLOOR_COLLIDERS,
  ];
}

function presidentialPalaceGrandStairColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const steps = Array.from({ length: 24 }, (_, index) => {
    const top = 0.72 + (index + 1) * 0.27;
    return {
      kind: "box" as const,
      id: `grand-stair-step-${index}`,
      centerLocalM: {
        x: 7.0 - (index + 0.5) * 0.34,
        y: top / 2,
        z: -2.4,
      },
      sizeM: { x: 0.36, y: top, z: 4.8 },
    };
  });
  const courtyardEntry = presidentialPalaceEntryColliders(
    6,
    "courtyard-entry",
    4.2,
    false,
  );
  const daukantoEntry = presidentialPalaceEntryColliders(
    20,
    "daukanto-entry",
    3.6,
    true,
  );
  return [...steps, ...courtyardEntry, ...daukantoEntry];
}

function presidentialPalaceEntryColliders(
  edgeIndex: number,
  id: string,
  gapWidthM: number,
  includeThroat: boolean,
): readonly AuthoredLandmarkBoxCollider[] {
  const start = PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M[edgeIndex]!;
  const end = PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M[
    (edgeIndex + 1) % PRESIDENTIAL_PALACE_OUTLINE_LOCAL_M.length
  ]!;
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  const length = Math.hypot(deltaX, deltaZ);
  const inwardX = -deltaZ / length;
  const inwardZ = deltaX / length;
  const midpointX = (start[0] + end[0]) / 2;
  const midpointZ = (start[1] + end[1]) / 2;
  const entrySteps: AuthoredLandmarkBoxCollider[] = ([
    [1.20, 0.18],
    [0.78, 0.38],
    [0.36, 0.58],
  ] as const).map(([outset, height], index) => ({
    kind: "box" as const,
    id: `${id}-step-${index}`,
    centerLocalM: {
      x: midpointX - inwardX * outset,
      y: height / 2,
      z: midpointZ - inwardZ * outset,
    },
    sizeM: { x: gapWidthM + 0.55, y: height, z: 0.56 },
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
  }));
  if (includeThroat) {
    const rampRunM = 3.2;
    const rampRiseM = 0.64;
    const rampAngleRadians = Math.atan2(rampRiseM, rampRunM);
    const rampThicknessM = 0.10;
    entrySteps.push({
      kind: "box",
      id: `${id}-walk-ramp`,
      centerLocalM: {
        x: midpointX + inwardX * 0.2,
        y: 0.40 - Math.cos(rampAngleRadians) * rampThicknessM / 2,
        z: midpointZ + inwardZ * 0.2,
      },
      sizeM: {
        x: gapWidthM - 0.35,
        y: rampThicknessM,
        z: Math.hypot(rampRunM, rampRiseM),
      },
      rotationXRadians: -rampAngleRadians,
      rotationYRadians: -Math.atan2(deltaZ, deltaX),
    });
    entrySteps.push({
      kind: "box",
      id: `${id}-throat`,
      centerLocalM: {
        x: midpointX + inwardX * 1.8,
        y: 0.66,
        z: midpointZ + inwardZ * 1.8,
      },
      sizeM: { x: gapWidthM, y: 0.12, z: 4.4 },
      rotationYRadians: -Math.atan2(deltaZ, deltaX),
    });
  }
  return entrySteps;
}

type PresidentialPartitionZ = readonly [
  id: string,
  z: number,
  minimumX: number,
  maximumX: number,
  doorX: number,
];

function presidentialPalacePartitionZColliders(
  partition: PresidentialPartitionZ,
  bottom: number,
  height: number,
): readonly AuthoredLandmarkBoxCollider[] {
  const [id, z, minimumX, maximumX, doorX] = partition;
  const doorWidth = 2.25;
  const openingHeight = 3.35;
  const leftEnd = doorX - doorWidth / 2;
  const rightStart = doorX + doorWidth / 2;
  return [
    {
      kind: "box",
      id: `${id}-west`,
      centerLocalM: { x: (minimumX + leftEnd) / 2, y: bottom + height / 2, z },
      sizeM: { x: leftEnd - minimumX, y: height, z: 0.30 },
    },
    {
      kind: "box",
      id: `${id}-east`,
      centerLocalM: { x: (rightStart + maximumX) / 2, y: bottom + height / 2, z },
      sizeM: { x: maximumX - rightStart, y: height, z: 0.30 },
    },
    {
      kind: "box",
      id: `${id}-lintel`,
      centerLocalM: {
        x: doorX,
        y: bottom + openingHeight + (height - openingHeight) / 2,
        z,
      },
      sizeM: { x: doorWidth, y: height - openingHeight, z: 0.30 },
    },
  ];
}

function presidentialPalacePartitionXColliders(
  id: string,
  x: number,
  minimumZ: number,
  maximumZ: number,
  doorZ: number,
): readonly AuthoredLandmarkBoxCollider[] {
  const bottom = 7.22;
  const height = 6.48;
  const doorWidth = 2.25;
  const openingHeight = 3.35;
  const southEnd = doorZ - doorWidth / 2;
  const northStart = doorZ + doorWidth / 2;
  return [
    {
      kind: "box",
      id: `${id}-south`,
      centerLocalM: { x, y: bottom + height / 2, z: (minimumZ + southEnd) / 2 },
      sizeM: { x: 0.30, y: height, z: southEnd - minimumZ },
    },
    {
      kind: "box",
      id: `${id}-north`,
      centerLocalM: { x, y: bottom + height / 2, z: (northStart + maximumZ) / 2 },
      sizeM: { x: 0.30, y: height, z: maximumZ - northStart },
    },
    {
      kind: "box",
      id: `${id}-lintel`,
      centerLocalM: {
        x,
        y: bottom + openingHeight + (height - openingHeight) / 2,
        z: doorZ,
      },
      sizeM: { x: 0.30, y: height - openingHeight, z: doorWidth },
    },
  ];
}

function presidentialPalaceInteriorWallColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const ground = [
    ["ground-service-gallery", -33.3, -9.0, 6.0, -4.0],
    ["ground-delegations-press", -24.0, -17.0, 1.0, -7.0],
    ["ground-press-stair", -12.0, -12.0, 5.0, -4.0],
    ["ground-stair-dining", 1.0, -9.0, 10.0, 0.0],
    ["ground-dining-maps", 14.0, -7.0, 12.0, 2.0],
    ["ground-maps-reception", 25.5, -4.0, 19.0, 7.0],
  ] as const satisfies readonly PresidentialPartitionZ[];
  const upper = [
    ["upper-service-delegations", -32.5, -13.5, 3.0, -7.0],
    ["upper-delegations-meeting", -24.0, -17.0, 1.0, -7.0],
    ["upper-meeting-suite", -13.0, -12.0, 5.0, -4.0],
    ["upper-suite-negotiation", -3.0, -9.0, 9.0, -1.5],
    ["upper-negotiation-salons", 10.0, -7.0, 11.0, 2.0],
    ["upper-salons-gallery", 20.0, -5.0, 14.0, 4.0],
    ["upper-gallery-white-hall", 27.5, -3.0, 20.0, 8.0],
  ] as const satisfies readonly PresidentialPartitionZ[];
  return [
    ...ground.flatMap((partition) =>
      presidentialPalacePartitionZColliders(partition, 0.72, 6.18)
    ),
    ...upper.flatMap((partition) =>
      presidentialPalacePartitionZColliders(partition, 7.22, 6.48)
    ),
    ...presidentialPalacePartitionXColliders(
      "upper-blue-green-divider",
      2.5,
      11.0,
      19.0,
      15.0,
    ),
    ...presidentialPalacePartitionXColliders(
      "upper-presidential-suite-divider",
      -1.0,
      -11.5,
      -4.5,
      -8.0,
    ),
  ];
}

/**
 * Enterable two-storey state rooms inside the exact GRPK palace shell.
 *
 * The secured room plan is not public. The public ceremonial programme is
 * represented as connected inset rooms: press and delegation spaces below,
 * then the White, Blue, Green, Negotiation and presidential rooms above. The
 * courtyard threshold, 24-step main stair and every floor plate match the
 * corresponding reproducible Blender asset.
 */
export const VILNIUS_PRESIDENTIAL_PALACE_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "presidential-palace",
    tileId: "2:2",
    buildingId: PRESIDENTIAL_PALACE_BUILDING_ID,
    origin: Object.freeze({ x: 523.312, y: 17.237, z: 569.693 }),
    rotationYRadians: -2.305986441,
    colliders: Object.freeze([
      ...presidentialPalaceExteriorColliders(),
      ...presidentialPalacePorticoColliders(),
      ...presidentialPalaceFloorColliders(),
      ...presidentialPalaceGrandStairColliders(),
      ...presidentialPalaceInteriorWallColliders(),
    ]),
  });

/**
 * Runtime-local primitives for the Town Hall public lobby.
 *
 * X/Z are Town Hall model-local runtime axes, Y is metres above the model
 * origin, and the whole set receives the same world yaw as the visual GLB.
 * The central 3.6 m facade bay is intentionally left open. Four 0.24 m steps
 * lead through a narrow vestibule into the broad reference-backed public
 * lobby. The lobby's main 30 × 8.8 m floor plus its 4.8 × 2.4 m entrance
 * throat approximates the official 280 m² venue area. Four piers and the
 * ceremonial stair retain clear central circulation while the remaining
 * facade and columns block movement. The origin elevation samples the
 * checked-in terrain at the square-facing edge of the lowest step so each
 * riser stays within the controller's documented 0.34 m autostep envelope.
 */
export const VILNIUS_TOWN_HALL_COLLISION: AuthoredLandmarkCollisionSpec =
  Object.freeze({
    landmarkId: "vilnius-town-hall",
    tileId: "2:4",
    buildingId: TOWN_HALL_BUILDING_ID,
    origin: Object.freeze({
      x: 591.644,
      y: 33.913,
      z: 1135.509,
    }),
    rotationYRadians: -0.429655,
    colliders: Object.freeze([
      {
        kind: "box" as const,
        id: "plinth-floor",
        centerLocalM: { x: 0, y: 0.48, z: 1.5 },
        sizeM: { x: 38, y: 0.96, z: 33 },
      },
      {
        kind: "box" as const,
        id: "lobby-main-finish-floor",
        centerLocalM: { x: 0, y: 0.99, z: -7.6 },
        sizeM: { x: 30, y: 0.06, z: 8.8 },
      },
      {
        kind: "box" as const,
        id: "lobby-vestibule-finish-floor",
        centerLocalM: { x: 0, y: 0.99, z: -13.2 },
        sizeM: { x: 4.8, y: 0.06, z: 2.4 },
      },
      {
        kind: "box" as const,
        id: "front-west-wall",
        centerLocalM: { x: -10.2, y: 3.18, z: -14.25 },
        sizeM: { x: 16.8, y: 4.44, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "front-east-wall",
        centerLocalM: { x: 10.2, y: 3.18, z: -14.25 },
        sizeM: { x: 16.8, y: 4.44, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "west-outer-wall",
        centerLocalM: { x: -18.35, y: 3.18, z: 1.5 },
        sizeM: { x: 0.5, y: 4.44, z: 32 },
      },
      {
        kind: "box" as const,
        id: "east-outer-wall",
        centerLocalM: { x: 18.35, y: 3.18, z: 1.5 },
        sizeM: { x: 0.5, y: 4.44, z: 32 },
      },
      {
        kind: "box" as const,
        id: "rear-outer-wall",
        centerLocalM: { x: 0, y: 3.18, z: 17.25 },
        sizeM: { x: 37.2, y: 4.44, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "upper-shell",
        centerLocalM: { x: 0, y: 9.45, z: 1.5 },
        sizeM: { x: 37.2, y: 8.1, z: 32 },
      },
      {
        kind: "box" as const,
        id: "vestibule-west-wall",
        centerLocalM: { x: -2.5, y: 3.18, z: -13.2 },
        sizeM: { x: 0.25, y: 4.44, z: 2.4 },
      },
      {
        kind: "box" as const,
        id: "vestibule-east-wall",
        centerLocalM: { x: 2.5, y: 3.18, z: -13.2 },
        sizeM: { x: 0.25, y: 4.44, z: 2.4 },
      },
      {
        kind: "box" as const,
        id: "main-hall-west-wall",
        centerLocalM: { x: -15.15, y: 3.18, z: -7.6 },
        sizeM: { x: 0.25, y: 4.44, z: 8.8 },
      },
      {
        kind: "box" as const,
        id: "main-hall-east-wall",
        centerLocalM: { x: 15.15, y: 3.18, z: -7.6 },
        sizeM: { x: 0.25, y: 4.44, z: 8.8 },
      },
      {
        kind: "box" as const,
        id: "lobby-back-west-wall",
        centerLocalM: { x: -9.45, y: 3.18, z: -3.2 },
        sizeM: { x: 11.1, y: 4.44, z: 0.45 },
      },
      {
        kind: "box" as const,
        id: "lobby-back-east-wall",
        centerLocalM: { x: 9.45, y: 3.18, z: -3.2 },
        sizeM: { x: 11.1, y: 4.44, z: 0.45 },
      },
      {
        kind: "box" as const,
        id: "stair-west-wall",
        centerLocalM: { x: -4.1, y: 3.18, z: -0.05 },
        sizeM: { x: 0.25, y: 4.44, z: 6.3 },
      },
      {
        kind: "box" as const,
        id: "stair-east-wall",
        centerLocalM: { x: 4.1, y: 3.18, z: -0.05 },
        sizeM: { x: 0.25, y: 4.44, z: 6.3 },
      },
      {
        kind: "box" as const,
        id: "stair-rear-wall",
        centerLocalM: { x: 0, y: 3.18, z: 3.2 },
        sizeM: { x: 8.4, y: 4.44, z: 0.45 },
      },
      ...[-5.2, 5.2].flatMap((x) =>
        [-6.2, -9.2].map((z) => ({
          kind: "box" as const,
          id: `lobby-pier-${x}-${z}`,
          centerLocalM: { x, y: 2.81, z },
          sizeM: { x: 1, y: 3.58, z: 1 },
        }))
      ),
      ...Array.from({ length: 8 }, (_, index) => {
        const topY = 1.26 + index * 0.24;
        return {
          kind: "box" as const,
          id: `interior-step-${index}`,
          centerLocalM: {
            x: 0,
            y: topY / 2,
            z: -(2.85 - index * 0.55),
          },
          sizeM: { x: 7.2, y: topY, z: 0.6 },
        };
      }),
      {
        kind: "box" as const,
        id: "interior-landing",
        centerLocalM: { x: 0, y: 1.47, z: 2.15 },
        sizeM: { x: 7.2, y: 2.94, z: 1.75 },
      },
      {
        kind: "box" as const,
        id: "front-step-0",
        centerLocalM: { x: 0, y: 0.12, z: -18.6 },
        sizeM: { x: 36.4, y: 0.24, z: 8.2 },
      },
      {
        kind: "box" as const,
        id: "front-step-1",
        centerLocalM: { x: 0, y: 0.24, z: -18.1 },
        sizeM: { x: 35.6, y: 0.48, z: 7.2 },
      },
      {
        kind: "box" as const,
        id: "front-step-2",
        centerLocalM: { x: 0, y: 0.36, z: -17.6 },
        sizeM: { x: 34.8, y: 0.72, z: 6.2 },
      },
      {
        kind: "box" as const,
        id: "front-step-3",
        centerLocalM: { x: 0, y: 0.48, z: -17.1 },
        sizeM: { x: 34, y: 0.96, z: 5.2 },
      },
      ...[-13.4, -8.05, -2.7, 2.7, 8.05, 13.4].map((x) => ({
        kind: "cylinder" as const,
        id: `portico-column-${x}`,
        centerLocalM: { x, y: 6.42, z: -17.35 },
        heightM: 10.65,
        radiusM: 0.88,
      })),
      {
        kind: "box" as const,
        id: "portico-entablature",
        centerLocalM: { x: 0, y: 15.25, z: -17 },
        sizeM: { x: 35.6, y: 7.1, z: 6.2 },
      },
    ]),
  });

/**
 * Public heritage route through the 1889 Bank of Lithuania palace.
 *
 * The source footprint is a single closed prism. This compound replacement
 * opens the documented Gedimino Avenue entrance, preserves the historic hall,
 * and continues through a narrow rear range to an interpreted old-vault
 * exhibit. It deliberately models no current secure circulation or operating
 * storage: only spaces documented by the Bank and its Money Museum.
 */
export const VILNIUS_LIETUVOS_BANKAS_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "lietuvos-bankas",
    tileId: "1:0",
    buildingId: LIETUVOS_BANKAS_BUILDING_ID,
    origin: Object.freeze({
      x: 337.338,
      y: 16.767,
      z: 242.131,
    }),
    rotationYRadians: -0.292447,
    colliders: Object.freeze([
      // The streamed terrain falls by roughly 0.33 m from the footprint
      // centroid to the Gedimino Avenue doorway. Four shallow granite steps
      // bridge that surveyed seam without asking the 0.34 m character
      // autostep to climb the full finished-floor height in one tick.
      {
        kind: "box" as const,
        id: "entrance-step-0",
        centerLocalM: { x: 4.5, y: -0.28, z: -27.4 },
        sizeM: { x: 5.8, y: 0.1, z: 1.2 },
      },
      {
        kind: "box" as const,
        id: "entrance-step-1",
        centerLocalM: { x: 4.5, y: -0.145, z: -26.2 },
        sizeM: { x: 5.5, y: 0.16, z: 1.2 },
      },
      {
        kind: "box" as const,
        id: "entrance-step-2",
        centerLocalM: { x: 4.5, y: 0, z: -25 },
        sizeM: { x: 5.2, y: 0.18, z: 1.2 },
      },
      {
        kind: "box" as const,
        id: "entrance-step-3",
        centerLocalM: { x: 4.5, y: 0.165, z: -23.6 },
        sizeM: { x: 4.8, y: 0.15, z: 1.6 },
      },
      {
        kind: "box" as const,
        id: "main-floor",
        centerLocalM: { x: 4.5, y: 0.12, z: -3 },
        sizeM: { x: 56, y: 0.24, z: 39 },
      },
      {
        kind: "box" as const,
        id: "front-west-wall",
        centerLocalM: { x: -10.45, y: 3.65, z: -22.5 },
        sizeM: { x: 26.1, y: 7.3, z: 0.55 },
      },
      {
        kind: "box" as const,
        id: "front-east-wall",
        centerLocalM: { x: 19.45, y: 3.65, z: -22.5 },
        sizeM: { x: 26.1, y: 7.3, z: 0.55 },
      },
      {
        kind: "box" as const,
        id: "front-door-lintel",
        centerLocalM: { x: 4.5, y: 5.95, z: -22.5 },
        sizeM: { x: 3.8, y: 2.7, z: 0.55 },
      },
      {
        kind: "box" as const,
        id: "west-wall",
        centerLocalM: { x: -23.5, y: 3.65, z: -3 },
        sizeM: { x: 0.55, y: 7.3, z: 39 },
      },
      {
        kind: "box" as const,
        id: "east-wall",
        centerLocalM: { x: 32.5, y: 3.65, z: -3 },
        sizeM: { x: 0.55, y: 7.3, z: 39 },
      },
      {
        kind: "box" as const,
        id: "rear-west-wall",
        centerLocalM: { x: -18.25, y: 3.65, z: 16.5 },
        sizeM: { x: 10.5, y: 7.3, z: 0.55 },
      },
      {
        kind: "box" as const,
        id: "rear-east-wall",
        centerLocalM: { x: 10.75, y: 3.65, z: 16.5 },
        sizeM: { x: 43.5, y: 7.3, z: 0.55 },
      },
      {
        kind: "box" as const,
        id: "rear-opening-lintel",
        centerLocalM: { x: -11, y: 5.975, z: 16.5 },
        sizeM: { x: 4.2, y: 2.65, z: 0.55 },
      },
      {
        kind: "box" as const,
        id: "main-ceiling",
        centerLocalM: { x: 4.5, y: 7.3, z: -3 },
        sizeM: { x: 56, y: 0.36, z: 39 },
      },
      {
        kind: "box" as const,
        id: "vault-wing-floor",
        centerLocalM: { x: -11, y: 0.12, z: 30 },
        sizeM: { x: 12, y: 0.24, z: 28 },
      },
      {
        kind: "box" as const,
        id: "vault-wing-west-wall",
        centerLocalM: { x: -17, y: 2.6, z: 30 },
        sizeM: { x: 0.5, y: 5.2, z: 28 },
      },
      {
        kind: "box" as const,
        id: "vault-wing-east-wall",
        centerLocalM: { x: -5, y: 2.6, z: 30 },
        sizeM: { x: 0.5, y: 5.2, z: 28 },
      },
      {
        kind: "box" as const,
        id: "vault-wing-rear-wall",
        centerLocalM: { x: -11, y: 2.6, z: 44 },
        sizeM: { x: 12, y: 5.2, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "vault-wing-ceiling",
        centerLocalM: { x: -11, y: 5.2, z: 30 },
        sizeM: { x: 12, y: 0.35, z: 28 },
      },
      {
        kind: "box" as const,
        id: "vault-threshold-west",
        centerLocalM: { x: -14.85, y: 2.5, z: 31 },
        sizeM: { x: 4.3, y: 5, z: 0.48 },
      },
      {
        kind: "box" as const,
        id: "vault-threshold-east",
        centerLocalM: { x: -7.15, y: 2.5, z: 31 },
        sizeM: { x: 4.3, y: 5, z: 0.48 },
      },
      {
        kind: "box" as const,
        id: "vault-threshold-lintel",
        centerLocalM: { x: -11, y: 4.4, z: 31 },
        sizeM: { x: 3.4, y: 1.2, z: 0.48 },
      },
      {
        kind: "box" as const,
        id: "open-vault-door",
        centerLocalM: { x: -12.25, y: 2.05, z: 30.15 },
        sizeM: { x: 3.25, y: 4, z: 0.42 },
        rotationYRadians: Math.PI * 58 / 180,
      },
      ...[-16.25, -5.75].map((x) => ({
        kind: "box" as const,
        id: `safe-cabinet-${x}`,
        centerLocalM: { x, y: 1.95, z: 37.7 },
        sizeM: { x: 1.15, y: 3.75, z: 10.5 },
      })),
      ...[-14.8, 23.3].map((x, index) => ({
        kind: "cylinder" as const,
        id: `ceramic-furnace-${index}`,
        centerLocalM: { x, y: 2.73, z: -5.5 },
        heightM: 5.45,
        radiusM: 1.18,
      })),
    ]),
  });

/**
 * Public visitor route for the Vilnius Television Tower.
 *
 * The checked-in GRPK footprint is a single circular mass. Replacing that
 * prism with this compound collision opens the south-facing visitor pavilion,
 * retains the concrete shaft, and supplies a stable floor and perimeter at
 * the 67th-floor observation deck. The lift itself is an authored transfer;
 * its closed ground and observation doors remain physical walls.
 */
export const VILNIUS_TV_TOWER_COLLISION: AuthoredLandmarkCollisionSpec =
  Object.freeze({
    landmarkId: "vilnius-tv-tower",
    tileId: "-16:0",
    buildingId: VILNIUS_TV_TOWER_BUILDING_ID,
    origin: Object.freeze({
      x: -4079.573,
      y: 90,
      z: 210.149,
    }),
    rotationYRadians: -0.315446,
    colliders: Object.freeze([
      {
        kind: "cylinder" as const,
        id: "foundation",
        centerLocalM: { x: 0, y: 0.12, z: 0 },
        heightM: 0.24,
        radiusM: 19,
      },
      {
        kind: "cylinder" as const,
        id: "concrete-shaft",
        centerLocalM: { x: 0, y: 82.5, z: 0 },
        heightM: 155,
        radiusM: 7.5,
      },
      {
        kind: "box" as const,
        id: "lobby-floor",
        centerLocalM: { x: 0, y: 0.1, z: 24 },
        sizeM: { x: 12, y: 0.2, z: 12 },
      },
      {
        kind: "box" as const,
        id: "lobby-west-wall",
        centerLocalM: { x: -5.82, y: 2.25, z: 24 },
        sizeM: { x: 0.36, y: 4.5, z: 12 },
      },
      {
        kind: "box" as const,
        id: "lobby-east-wall",
        centerLocalM: { x: 5.82, y: 2.25, z: 24 },
        sizeM: { x: 0.36, y: 4.5, z: 12 },
      },
      {
        kind: "box" as const,
        id: "lobby-front-west-wall",
        centerLocalM: { x: -3.88, y: 2.25, z: 29.82 },
        sizeM: { x: 4.25, y: 4.5, z: 0.36 },
      },
      {
        kind: "box" as const,
        id: "lobby-front-east-wall",
        centerLocalM: { x: 3.88, y: 2.25, z: 29.82 },
        sizeM: { x: 4.25, y: 4.5, z: 0.36 },
      },
      {
        kind: "box" as const,
        id: "lobby-lift-wall",
        centerLocalM: { x: 0, y: 2.25, z: 18.18 },
        sizeM: { x: 12, y: 4.5, z: 0.36 },
      },
      {
        kind: "box" as const,
        id: "lobby-ceiling",
        centerLocalM: { x: 0, y: 4.4, z: 24 },
        sizeM: { x: 12, y: 0.4, z: 12 },
      },
      {
        kind: "box" as const,
        id: "lobby-ticket-desk",
        centerLocalM: { x: -3.85, y: 0.65, z: 22.4 },
        sizeM: { x: 2.5, y: 1.3, z: 1.1 },
      },
      {
        kind: "cylinder" as const,
        id: "observation-floor",
        centerLocalM: { x: 0, y: 166.18, z: 0 },
        heightM: 0.36,
        radiusM: 17.75,
      },
      {
        kind: "cylinder" as const,
        id: "observation-core",
        centerLocalM: { x: 0, y: 169.1, z: 0 },
        heightM: 5.5,
        radiusM: 4.2,
      },
      {
        kind: "cylinder" as const,
        id: "observation-ceiling",
        centerLocalM: { x: 0, y: 172.15, z: 0 },
        heightM: 0.4,
        radiusM: 17.75,
      },
      ...Array.from({ length: 24 }, (_, index) => {
        const angle = index * Math.PI * 2 / 24;
        return {
          kind: "box" as const,
          id: `observation-glazing-${index}`,
          centerLocalM: {
            x: Math.cos(angle) * 17.55,
            y: 169.08,
            z: Math.sin(angle) * 17.55,
          },
          sizeM: { x: 4.7, y: 5.8, z: 0.22 },
          rotationYRadians: Math.PI / 2 - angle,
        };
      }),
    ]),
  });

/**
 * Walkable public atrium for the Gedimino 9 shopping centre.
 *
 * The source GRPK footprint is one closed historic-city-hall prism. This
 * compound replacement leaves the central Gedimino Avenue portal open, keeps
 * a continuous ground floor, and exposes the first-floor galleries through a
 * broad central stair. Local runtime +Z is the avenue/front side of the Blender
 * model. Local X follows the official avenue edge; the old 73.83 degree yaw
 * incorrectly put the footprint depth across the street frontage.
 */
export const VILNIUS_GEDIMINO_9_COLLISION: AuthoredLandmarkCollisionSpec =
  Object.freeze({
    landmarkId: "gedimino-9-shopping-centre",
    tileId: "0:0",
    buildingId: GEDIMINO_9_BUILDING_ID,
    origin: Object.freeze({
      x: 241.26,
      y: 20,
      z: 111.797,
    }),
    rotationYRadians: -0.282217,
    colliders: Object.freeze([
      ...[
        { id: "front", x: -4, z: 36.5, width: 48, depth: 31 },
        { id: "centre", x: 0, z: 7, width: 56, depth: 28 },
        { id: "middle", x: -4, z: -16, width: 48, depth: 18 },
        { id: "rear", x: -19.5, z: -38.5, width: 17, depth: 27 },
      ].map((section) => ({
        kind: "box" as const,
        id: `ground-floor-${section.id}`,
        centerLocalM: { x: section.x, y: 0.18, z: section.z },
        sizeM: { x: section.width, y: 0.36, z: section.depth },
      })),
      {
        kind: "box" as const,
        id: "west-outer-wall",
        centerLocalM: { x: -27.5, y: 9.25, z: 0.25 },
        sizeM: { x: 1, y: 18.5, z: 104 },
      },
      ...[
        { id: "east-front", x: 19.5, z: 36.5, depth: 31 },
        { id: "east-centre", x: 27.5, z: 7, depth: 28 },
        { id: "east-middle", x: 19.5, z: -16, depth: 18 },
        { id: "east-rear", x: -11.5, z: -38.5, depth: 27 },
      ].map((wall) => ({
        kind: "box" as const,
        id: wall.id,
        centerLocalM: { x: wall.x, y: 9.25, z: wall.z },
        sizeM: { x: 1, y: 18.5, z: wall.depth },
      })),
      ...[
        { id: "front-step", x: 24, z: 21.5, width: 8 },
        { id: "centre-step", x: 24, z: -7.5, width: 8 },
        { id: "middle-step", x: 4.5, z: -25.5, width: 31 },
        { id: "rear-outer-wall", x: -19.5, z: -51.5, width: 17 },
      ].map((wall) => ({
        kind: "box" as const,
        id: wall.id,
        centerLocalM: { x: wall.x, y: 9.25, z: wall.z },
        sizeM: { x: wall.width, y: 18.5, z: 1 },
      })),
      {
        kind: "box" as const,
        id: "front-west-wall",
        centerLocalM: { x: -18, y: 9.25, z: 51.5 },
        sizeM: { x: 20, y: 18.5, z: 1 },
      },
      {
        kind: "box" as const,
        id: "front-east-wall",
        centerLocalM: { x: 10, y: 9.25, z: 51.5 },
        sizeM: { x: 20, y: 18.5, z: 1 },
      },
      {
        kind: "box" as const,
        id: "first-gallery-west",
        centerLocalM: { x: -23, y: 5.05, z: 23 },
        sizeM: { x: 8, y: 0.38, z: 46 },
      },
      {
        kind: "box" as const,
        id: "first-gallery-east",
        centerLocalM: { x: 16, y: 5.05, z: 23 },
        sizeM: { x: 8, y: 0.38, z: 46 },
      },
      {
        kind: "box" as const,
        id: "first-gallery-rear",
        centerLocalM: { x: -3.5, y: 5.05, z: 2 },
        sizeM: { x: 31, y: 0.38, z: 8 },
      },
      ...Array.from({ length: 18 }, (_, index) => {
        const progress = index / 17;
        return {
          kind: "box" as const,
          id: `central-stair-${index}`,
          centerLocalM: {
            x: -4,
            y: 0.15 + progress * 4.85,
            z: 34 - progress * 14,
          },
          // One broad walkable envelope supports the paired authored escalator
          // flights and their central divider without leaving false visual
          // surfaces outside physics.
          sizeM: { x: 12.4, y: 0.3, z: 0.75 },
        };
      }),
      {
        kind: "box" as const,
        id: "lift-core",
        centerLocalM: { x: -4, y: 6.75, z: 5 },
        sizeM: { x: 5.2, y: 13.5, z: 5.2 },
      },
      ...[-23, 16].flatMap((x) =>
        [36, 23, 10].map((z, index) => ({
          kind: "box" as const,
          id: `shopfront-${x}-${index}`,
          centerLocalM: { x, y: 2.15, z },
          sizeM: { x: 0.42, y: 4.3, z: 10 },
        }))
      ),
    ]),
  });

const HALES_MARKET_AXIS_ANGLE = Math.PI * 24 / 180;
const HALES_MARKET_MAIN_ENTRY_EDGE = 26;
const HALES_MARKET_SIDE_ENTRY_EDGE = 16;

/**
 * Official GRPK footprint transformed into the Halės model's local X/Z axes.
 * Keeping the irregular street edge here prevents a rectangular compound from
 * reaching back into Pylimo g. while the source building prism is suppressed.
 */
const HALES_MARKET_OUTLINE_LOCAL_M = Object.freeze([
  [7.402, -28.186],
  [6.667, -36.609],
  [6.219, -39.606],
  [19.068, -43.697],
  [20.509, -44.172],
  [21.827, -44.606],
  [24.392, -45.453],
  [25.486, -45.813],
  [25.722, -45.89],
  [50.399, -56.947],
  [51.023, -55.667],
  [52.806, -52.005],
  [54.059, -49.433],
  [56.61, -44.194],
  [58.512, -40.289],
  [58.957, -39.375],
  [71.739, -9.616],
  [78.275, 5.604],
  [76.711, 6.377],
  [65.777, 10.99],
  [63.555, 5.721],
  [59.716, 7.342],
  [38.54, 16.276],
  [36.801, 17.01],
  [11.941, 27.498],
  [9.718, 31.352],
  [-26.584, 45.884],
  [-54.516, 57.065],
  [-68.01, 25.284],
  [-53.957, 19.317],
  [-55.369, 15.791],
  [-57.493, 16.868],
  [-59.66, 16.685],
  [-61.057, 14.624],
  [-63.687, 5.304],
  [-64.095, 1.595],
  [-64.095, -23.402],
  [-59.329, -23.401],
] as const);

function halesMarketAxisPoint(
  along: number,
  across = 0,
): Readonly<{ x: number; z: number }> {
  const cosine = Math.cos(HALES_MARKET_AXIS_ANGLE);
  const sine = Math.sin(HALES_MARKET_AXIS_ANGLE);
  return {
    x: along * cosine - across * sine,
    // Blender's hall centre is (0, -4); glTF converts -Y to runtime +Z.
    z: 4 - along * sine - across * cosine,
  };
}

function halesMarketRunCollider(
  id: string,
  start: readonly [number, number],
  end: readonly [number, number],
  centerY = 7.9,
  height = 15.8,
  thickness = 0.58,
): AuthoredLandmarkBoxCollider {
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  return {
    kind: "box",
    id,
    centerLocalM: {
      x: (start[0] + end[0]) / 2,
      y: centerY,
      z: (start[1] + end[1]) / 2,
    },
    sizeM: {
      x: Math.hypot(deltaX, deltaZ),
      y: height,
      z: thickness,
    },
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
  };
}

function halesMarketSplitEdge(
  start: readonly [number, number],
  end: readonly [number, number],
  gapWidth: number,
): readonly [readonly [number, number], readonly [number, number]] {
  const length = Math.hypot(end[0] - start[0], end[1] - start[1]);
  const halfFraction = gapWidth / length / 2;
  const pointAt = (progress: number): readonly [number, number] => [
    start[0] + (end[0] - start[0]) * progress,
    start[1] + (end[1] - start[1]) * progress,
  ];
  return [pointAt(0.5 - halfFraction), pointAt(0.5 + halfFraction)];
}

function halesMarketExteriorColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  for (const [index, start] of HALES_MARKET_OUTLINE_LOCAL_M.entries()) {
    const end = HALES_MARKET_OUTLINE_LOCAL_M[
      (index + 1) % HALES_MARKET_OUTLINE_LOCAL_M.length
    ]!;
    if (
      index === HALES_MARKET_MAIN_ENTRY_EDGE ||
      index === HALES_MARKET_SIDE_ENTRY_EDGE
    ) {
      const gapWidth = index === HALES_MARKET_MAIN_ENTRY_EDGE ? 8.4 : 5.4;
      const [first, second] = halesMarketSplitEdge(start, end, gapWidth);
      colliders.push(
        halesMarketRunCollider(`outer-wall-${index}-a`, start, first),
        halesMarketRunCollider(`outer-wall-${index}-b`, second, end),
      );
      const openingHeight = index === HALES_MARKET_MAIN_ENTRY_EDGE ? 7.1 : 4.8;
      const lintelHeight = 15.8 - openingHeight;
      colliders.push(
        halesMarketRunCollider(
          index === HALES_MARKET_MAIN_ENTRY_EDGE
            ? "main-entry-lintel"
            : "side-entry-lintel",
          first,
          second,
          openingHeight + lintelHeight / 2,
          lintelHeight,
        ),
      );
      continue;
    }
    colliders.push(halesMarketRunCollider(`outer-wall-${index}`, start, end));
  }
  return colliders;
}

/**
 * Enterable Baltas Tiltas food hall beneath the White Bridge deck.
 *
 * The perimeter follows the official curved GRPK footprint. Both axial door
 * edges stay open beneath their lintels; a polished-concrete floor, black
 * bridge columns, nine open-kitchen bays, the central bar and shared
 * tables provide stable player-scale collision. The unsupported upper/service
 * spaces remain closed behind the ceiling plate.
 */
export const VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "white-bridge",
    tileId: "-2:-3",
    buildingId: WHITE_BRIDGE_FOOD_HALL_BUILDING_ID,
    origin: Object.freeze({ x: -362.418, y: 11.568, z: -478.545 }),
    rotationYRadians: 0.832554,
    colliders: Object.freeze([
      ...whiteBridgeFoodHallWallColliders(),
      ...([
        [85.3, 0, 17.5, 18],
        [85.3, -15, 14, 12],
        [85.3, 15, 14, 12],
      ] as const).flatMap(([x, z, sizeX, sizeZ], index) => [
        {
          kind: "box" as const,
          id: `floor-${index}`,
          centerLocalM: {
            x,
            y: WHITE_BRIDGE_FOOD_HALL_FLOOR_Y - 0.10,
            z,
          },
          sizeM: { x: sizeX, y: 0.20, z: sizeZ },
        },
        {
          kind: "box" as const,
          id: `ceiling-${index}`,
          centerLocalM: {
            x,
            y: WHITE_BRIDGE_FOOD_HALL_CEILING_Y + 0.09,
            z,
          },
          sizeM: { x: sizeX, y: 0.18, z: sizeZ },
        },
      ]),
      ...([
        ["entry-apron-riverside", 73.05, -0.385, 0.8],
        ["entry-apron-lower", 73.65, -0.315, 0.8],
        ["entry-apron-approach", 74.25, -0.245, 0.8],
        ["entry-apron-low", 74.85, -0.175, 0.8],
        ["entry-apron-middle", 75.45, -0.105, 0.8],
        ["entry-threshold", 76.15, -0.035, 0.9],
      ] as const).map(([id, x, centerOffsetY, sizeX]) => ({
        kind: "box" as const,
        id,
        centerLocalM: {
          x,
          y: WHITE_BRIDGE_FOOD_HALL_FLOOR_Y + centerOffsetY,
          z: 0,
        },
        sizeM: { x: sizeX, y: 0.07, z: 5.4 },
      })),
      ...([
        [88.5, -16.4],
        [90.8, -12.3],
        [92.2, -8.2],
        [92.9, -4.1],
        [93.1, 0],
        [93, 4.1],
        [92.4, 8.2],
        [91, 12.3],
        [88.7, 16.4],
      ] as const).map(([x, z], index) => ({
        kind: "box" as const,
        id: `open-kitchen-${index}`,
        centerLocalM: {
          x,
          y: WHITE_BRIDGE_FOOD_HALL_FLOOR_Y + 0.575,
          z,
        },
        sizeM: { x: 1.75, y: 1.15, z: 2.75 },
      })),
      {
        kind: "cylinder" as const,
        id: "bar-coffee-island",
        centerLocalM: {
          x: 82.9,
          y: WHITE_BRIDGE_FOOD_HALL_FLOOR_Y + 0.54,
          z: 0,
        },
        heightM: 1.08,
        radiusM: 2.25,
      },
      ...([
        [80, -13],
        [80, 13],
        [88, -4.8],
        [88, 4.8],
      ] as const).map(([x, z], index) => ({
        kind: "cylinder" as const,
        id: `bridge-column-${index}`,
        centerLocalM: {
          x,
          y: (WHITE_BRIDGE_FOOD_HALL_FLOOR_Y +
            WHITE_BRIDGE_FOOD_HALL_CEILING_Y) / 2,
          z,
        },
        heightM: WHITE_BRIDGE_FOOD_HALL_CEILING_Y -
          WHITE_BRIDGE_FOOD_HALL_FLOOR_Y,
        radiusM: 0.42,
      })),
      ...([
        [79.25, -16.2],
        [80.45, -9.4],
        [78.85, -5.15],
        [78.85, 5.15],
        [80.45, 9.4],
        [79.25, 16.2],
        [85.85, -16.7],
        [85.85, 16.7],
      ] as const).map(([x, z], index) => ({
        kind: "cylinder" as const,
        id: `shared-table-${index}`,
        centerLocalM: {
          x,
          y: WHITE_BRIDGE_FOOD_HALL_FLOOR_Y + 0.41,
          z,
        },
        heightM: 0.82,
        radiusM: 0.62,
      })),
      ...([-11.8, 11.8] as const).map((z, index) => ({
        kind: "box" as const,
        id: `banquette-${index}`,
        centerLocalM: {
          x: 77.55,
          y: WHITE_BRIDGE_FOOD_HALL_FLOOR_Y + 0.36,
          z,
        },
        sizeM: { x: 1.45, y: 0.72, z: 8 },
      })),
    ]),
  });

/**
 * Outdoor land cover and three mapped park paths predate the current enclosed
 * food hall in the shared source tile. Retain them around the pavilion, but
 * cut their render surfaces at the surveyed GRPK wall line so the authored
 * polished-concrete slab is the only finish visible indoors.
 */
export const VILNIUS_WHITE_BRIDGE_FOOD_HALL_SURFACE_MASK:
  AuthoredInteriorSurfaceMask = Object.freeze({
    id: "white-bridge-food-hall",
    tileId: VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.tileId,
    outerWorldM: Object.freeze(
      WHITE_BRIDGE_FOOD_HALL_OUTLINE_LOCAL_M.map(([x, z]) => {
        const cosine = Math.cos(
          VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.rotationYRadians,
        );
        const sine = Math.sin(
          VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.rotationYRadians,
        );
        return Object.freeze({
          x: VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.origin.x +
            x * cosine + z * sine,
          z: VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.origin.z -
            x * sine + z * cosine,
        });
      }),
    ),
    transportFeatureIds: new Set([
      "osm:way:1185783660#0.0",
      "osm:way:620128374#0.0",
      "osm:way:620128375#0.0",
    ]),
  });

export const VILNIUS_AUTHORED_INTERIOR_SURFACE_MASKS:
  readonly AuthoredInteriorSurfaceMask[] = Object.freeze([
    VILNIUS_WHITE_BRIDGE_FOOD_HALL_SURFACE_MASK,
  ]);

/**
 * Enterable two-level Halės Market on its surveyed Pylimo/Bazilijonų outline.
 *
 * The ground plate and fixture islands leave a broad diagonal market aisle.
 * Twin public stairs reach food-and-bar galleries on both sides of the nave;
 * the footprint walls are split only at the two matching authored entrances.
 */
export const VILNIUS_HALES_MARKET_COLLISION: AuthoredLandmarkCollisionSpec =
  Object.freeze({
    landmarkId: "hales-market",
    tileId: "2:6",
    buildingId: HALES_MARKET_BUILDING_ID,
    origin: Object.freeze({ x: 578.809, y: 48.855, z: 1657.666 }),
    rotationYRadians: -1.43949,
    colliders: Object.freeze([
      ...halesMarketExteriorColliders(),
      ...Array.from({ length: 7 }, (_, index) => {
        const mainStart = HALES_MARKET_OUTLINE_LOCAL_M[
          HALES_MARKET_MAIN_ENTRY_EDGE
        ]!;
        const mainEnd = HALES_MARKET_OUTLINE_LOCAL_M[
          (HALES_MARKET_MAIN_ENTRY_EDGE + 1) %
            HALES_MARKET_OUTLINE_LOCAL_M.length
        ]!;
        const progress = index / 6;
        const distanceInside = 0.72 + index * 1.02;
        const top = -1.12 + progress * 1.38;
        const base = -1.55;
        return {
          kind: "box" as const,
          id: `main-threshold-step-${index}`,
          centerLocalM: {
            x: (mainStart[0] + mainEnd[0]) / 2 + 0.04 * distanceInside,
            y: (base + top) / 2,
            z: (mainStart[1] + mainEnd[1]) / 2 - 0.9992 * distanceInside,
          },
          sizeM: { x: 8, y: top - base, z: 1.16 },
          rotationYRadians: -Math.atan2(
            mainEnd[1] - mainStart[1],
            mainEnd[0] - mainStart[0],
          ),
        };
      }),
      {
        kind: "box" as const,
        id: "central-floor-plate",
        centerLocalM: { x: 0, y: 0.18, z: 4 },
        sizeM: { x: 100, y: 0.2, z: 36 },
        rotationYRadians: HALES_MARKET_AXIS_ANGLE,
      },
      ...[-1, 1].flatMap((side) => {
        const gallery = halesMarketAxisPoint(0, side * 18);
        const rail = halesMarketAxisPoint(0, side * 13.95);
        return [
          {
            kind: "box" as const,
            id: `upper-gallery-${side}`,
            centerLocalM: { ...gallery, y: 5.28 },
            sizeM: { x: 94, y: 0.34, z: 8 },
            rotationYRadians: HALES_MARKET_AXIS_ANGLE,
          },
          {
            kind: "box" as const,
            id: `gallery-inner-rail-${side}`,
            centerLocalM: { ...rail, y: 5.98 },
            sizeM: { x: 94, y: 1.25, z: 0.18 },
            rotationYRadians: HALES_MARKET_AXIS_ANGLE,
          },
        ];
      }),
      (() => {
        const center = halesMarketAxisPoint(-39.5);
        return {
          kind: "box" as const,
          id: "upper-gallery-bridge",
          centerLocalM: { ...center, y: 5.28 },
          sizeM: { x: 8, y: 0.34, z: 36 },
          rotationYRadians: HALES_MARKET_AXIS_ANGLE,
        };
      })(),
      ...([-1, 1] as const).flatMap((stairSide) => {
        const stairAlong = stairSide === -1 ? -24 : 23;
        return Array.from({ length: 18 }, (_, index) => {
          const progress = index / 17;
          const center = halesMarketAxisPoint(
            stairAlong,
            stairSide * (4 + progress * 11.5),
          );
          return {
            kind: "box" as const,
            id: `public-stair-${stairSide}-${index}`,
            centerLocalM: { ...center, y: 0.25 + progress * 4.95 },
            sizeM: { x: 5.4, y: 0.3, z: 0.86 },
            rotationYRadians: HALES_MARKET_AXIS_ANGLE,
          };
        });
      }),
      ...[
        ["produce-a", -37, -8.2],
        ["produce-b", -20, -8.2],
        ["fruit", -3, -8.2],
        ["farm-veg", 14, -8.2],
        ["cheese", -28, 8.2],
        ["bakery", -11, 8.2],
        ["meat", 6, 8.2],
        ["fish", 23, 8.2],
        ["pantry", 40, 8.2],
      ].map(([name, along, across]) => {
        const center = halesMarketAxisPoint(along as number, across as number);
        return {
          kind: "box" as const,
          id: `stall-${name}`,
          centerLocalM: { ...center, y: 0.65 },
          sizeM: { x: 11.7, y: 1.3, z: 4.05 },
          rotationYRadians: HALES_MARKET_AXIS_ANGLE,
        };
      }),
      ...[
        ["bukowski-bar", 23, -19, 31],
        ["petro-kitchen", 26, 19, 24],
        ["oldskul-cafe", -18, -19, 18],
        ["onigiri-counter", -13, 19, 20],
      ].map(([name, along, across, length]) => {
        const center = halesMarketAxisPoint(along as number, across as number);
        return {
          kind: "box" as const,
          id: `upper-counter-${name}`,
          centerLocalM: { ...center, y: 5.95 },
          sizeM: { x: length as number, y: 1.16, z: 2.2 },
          rotationYRadians: HALES_MARKET_AXIS_ANGLE,
        };
      }),
      ...Array.from({ length: 8 }, (_, index) => -42 + index * 12).flatMap(
        (along) => [-14, 14].map((across) => {
          const center = halesMarketAxisPoint(along, across);
          return {
            kind: "cylinder" as const,
            id: `hall-column-${along}-${across}`,
            centerLocalM: { ...center, y: 7.8 },
            heightM: 15.2,
            radiusM: 0.3,
          };
        }),
      ),
      {
        kind: "box" as const,
        id: "roof-collision-deck",
        centerLocalM: { x: 0, y: 15.82, z: 4 },
        sizeM: { x: 102, y: 0.5, z: 39 },
        rotationYRadians: HALES_MARKET_AXIS_ANGLE,
      },
    ]),
  });

/**
 * Enterable ground-floor supermarket at Mindaugo g. 11.
 *
 * The official footprint is split at the authored street entrance and the
 * sales floor uses compound fixture collision. The upper parking deck and
 * rear service mass remain closed; the fixture arrangement interprets the
 * operator-published programme and is not represented as a surveyed plan.
 */
export const VILNIUS_MINDAUGO_MAXIMA_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "mindaugo-maxima",
    tileId: "-2:4",
    buildingId: MINDAUGO_MAXIMA_BUILDING_ID,
    origin: Object.freeze({ x: -324.895, y: 60, z: 1193.707 }),
    rotationYRadians: 0.221968,
    colliders: Object.freeze([
      {
        kind: "box" as const,
        id: "sales-floor",
        centerLocalM: { x: 0, y: 0.12, z: 35 },
        sizeM: { x: 121, y: 0.24, z: 46 },
      },
      {
        kind: "box" as const,
        id: "sales-ceiling",
        centerLocalM: { x: 0, y: 5.18, z: 35 },
        sizeM: { x: 121, y: 0.35, z: 46 },
      },
      ...([-60.75, 60.75] as const).map((x, index) => ({
        kind: "box" as const,
        id: `sales-${index === 0 ? "west" : "east"}-wall`,
        centerLocalM: { x, y: 2.6, z: 35 },
        sizeM: { x: 0.5, y: 5.2, z: 46 },
      })),
      {
        kind: "box" as const,
        id: "sales-rear-wall",
        centerLocalM: { x: 0, y: 2.6, z: 12 },
        sizeM: { x: 121, y: 5.2, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "front-wall-west",
        centerLocalM: { x: -20.25, y: 2.6, z: 58 },
        sizeM: { x: 81.5, y: 5.2, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "front-wall-east",
        centerLocalM: { x: 48.25, y: 2.6, z: 58 },
        sizeM: { x: 25.5, y: 5.2, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "front-entry-lintel",
        centerLocalM: { x: 28, y: 4.075, z: 58 },
        sizeM: { x: 15, y: 2.25, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "closed-rear-service-mass",
        centerLocalM: { x: 0, y: 6.25, z: -1 },
        sizeM: { x: 122, y: 12.5, z: 26 },
      },
      {
        kind: "box" as const,
        id: "rear-parking-mass",
        centerLocalM: { x: -12, y: 5.75, z: -29 },
        sizeM: { x: 82, y: 11.5, z: 38 },
      },
      ...Array.from({ length: 10 }, (_, index) => ({
        kind: "box" as const,
        id: `staffed-checkout-${index + 1}`,
        centerLocalM: { x: -49 + index * 3.35, y: 0.475, z: 48 },
        sizeM: { x: 2.35, y: 0.95, z: 6.4 },
      })),
      ...([-43, -39, -35, -31] as const).flatMap((blenderY, row) =>
        [-10, -5.5, -1, 3.5, 8].map((x, column) => ({
          kind: "box" as const,
          id: `self-checkout-${row * 5 + column + 1}`,
          centerLocalM: { x, y: 0.825, z: 10 - blenderY },
          sizeM: { x: 0.95, y: 1.65, z: 0.95 },
        }))
      ),
      ...([-47, -34, -21, -8, 5, 18, 31] as const).map((x, index) => ({
        kind: "box" as const,
        id: `grocery-aisle-${index + 1}`,
        centerLocalM: { x, y: 0.925, z: 27 },
        sizeM: { x: 2.35, y: 1.85, z: 20 },
      })),
      {
        kind: "box" as const,
        id: "scan-and-go-bank",
        centerLocalM: { x: 56, y: 0.875, z: 36.5 },
        sizeM: { x: 2, y: 1.75, z: 33 },
      },
      {
        kind: "box" as const,
        id: "seasonal-non-food-island",
        centerLocalM: { x: 43, y: 0.625, z: 50 },
        sizeM: { x: 9, y: 1.25, z: 5 },
      },
      ...([23.2, 32.8] as const).map((x, index) => ({
        kind: "box" as const,
        id: `entry-security-gate-${index === 0 ? "west" : "east"}`,
        centerLocalM: { x, y: 0.59, z: 55.1 },
        sizeM: { x: 0.58, y: 1.18, z: 2.4 },
      })),
      {
        kind: "box" as const,
        id: "entrance-basket-stack",
        centerLocalM: { x: 37.8, y: 0.45, z: 55 },
        sizeM: { x: 1.8, y: 0.9, z: 1.5 },
      },
      {
        kind: "box" as const,
        id: "entrance-trolley-train",
        centerLocalM: { x: 48.5, y: 0.575, z: 55 },
        sizeM: { x: 4.5, y: 1.15, z: 2.2 },
      },
      ...([
        [41, 40],
        [51, 32],
        [42, 27],
      ] as const).map(([x, z], index) => ({
        kind: "box" as const,
        id: `produce-island-${index + 1}`,
        centerLocalM: { x, y: 0.575, z },
        sizeM: { x: 8.5, y: 1.15, z: 4.5 },
      })),
      ...([31, 14] as const).map((z, index) => ({
        kind: "box" as const,
        id: `refrigerated-west-${index + 1}`,
        centerLocalM: { x: -57.5, y: 1.075, z },
        sizeM: { x: 2.2, y: 2.15, z: 13 },
      })),
      ...([
        ["fresh-meat-fish", -39, 37],
        ["bakery-confectionery", -8, 21],
        ["dairy-frozen", 29, 43],
      ] as const).map(([id, x, width]) => ({
        kind: "box" as const,
        id: `rear-${id}`,
        centerLocalM: { x, y: 0.675, z: 14 },
        sizeM: { x: width, y: 1.35, z: 2.3 },
      })),
    ]),
  });

const DEMOKRATINE_MOKYKLA_ENTRY_EDGE = 2;
const DEMOKRATINE_MOKYKLA_ENTRY_PROGRESS = 0.68;
const DEMOKRATINE_MOKYKLA_ENTRY_WIDTH_M = 2.65;
const DEMOKRATINE_MOKYKLA_WALL_THICKNESS_M = 0.46;

/**
 * Official GRPK footprint transformed into the school's model-local axes.
 * Edge 2 is the long Žygimantų Street façade facing the Neris.
 */
export const VILNIUS_DEMOKRATINE_MOKYKLA_OUTLINE_LOCAL_M = Object.freeze([
  [0.609, 12.671],
  [-15.612, 14.341],
  [-14.721, 9.927],
  [-10.109, -8.234],
  [-7.418, -10.982],
  [16.108, -10.982],
  [16.108, 3.904],
  [4.793, 4.137],
  [1.859, 6.551],
] as const);

function demokratineMokyklaPointAt(
  start: readonly [number, number],
  end: readonly [number, number],
  progress: number,
): readonly [number, number] {
  return [
    start[0] + (end[0] - start[0]) * progress,
    start[1] + (end[1] - start[1]) * progress,
  ];
}

function demokratineMokyklaEntranceFrame(): Readonly<{
  point: readonly [number, number];
  tangent: readonly [number, number];
  inward: readonly [number, number];
  rotationYRadians: number;
}> {
  const start = VILNIUS_DEMOKRATINE_MOKYKLA_OUTLINE_LOCAL_M[
    DEMOKRATINE_MOKYKLA_ENTRY_EDGE
  ]!;
  const end = VILNIUS_DEMOKRATINE_MOKYKLA_OUTLINE_LOCAL_M[
    (DEMOKRATINE_MOKYKLA_ENTRY_EDGE + 1) %
      VILNIUS_DEMOKRATINE_MOKYKLA_OUTLINE_LOCAL_M.length
  ]!;
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  const length = Math.hypot(deltaX, deltaZ);
  return Object.freeze({
    point: demokratineMokyklaPointAt(
      start,
      end,
      DEMOKRATINE_MOKYKLA_ENTRY_PROGRESS,
    ),
    tangent: Object.freeze([deltaX / length, deltaZ / length] as const),
    inward: Object.freeze([-deltaZ / length, deltaX / length] as const),
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
  });
}

function demokratineMokyklaWallCollider(
  id: string,
  start: readonly [number, number],
  end: readonly [number, number],
  centerY = 6.23,
  height = 14.34,
): AuthoredLandmarkBoxCollider {
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  const length = Math.hypot(deltaX, deltaZ);
  const inwardX = -deltaZ / length;
  const inwardZ = deltaX / length;
  return {
    kind: "box",
    id,
    centerLocalM: {
      x: (start[0] + end[0]) / 2 +
        inwardX * DEMOKRATINE_MOKYKLA_WALL_THICKNESS_M / 2,
      y: centerY,
      z: (start[1] + end[1]) / 2 +
        inwardZ * DEMOKRATINE_MOKYKLA_WALL_THICKNESS_M / 2,
    },
    sizeM: {
      x: length,
      y: height,
      z: DEMOKRATINE_MOKYKLA_WALL_THICKNESS_M,
    },
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
  };
}

function demokratineMokyklaExteriorColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  for (
    const [index, start] of VILNIUS_DEMOKRATINE_MOKYKLA_OUTLINE_LOCAL_M
      .entries()
  ) {
    const end = VILNIUS_DEMOKRATINE_MOKYKLA_OUTLINE_LOCAL_M[
      (index + 1) % VILNIUS_DEMOKRATINE_MOKYKLA_OUTLINE_LOCAL_M.length
    ]!;
    if (index !== DEMOKRATINE_MOKYKLA_ENTRY_EDGE) {
      colliders.push(
        demokratineMokyklaWallCollider(`outer-wall-${index}`, start, end),
      );
      continue;
    }

    const length = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const halfFraction = DEMOKRATINE_MOKYKLA_ENTRY_WIDTH_M / length / 2;
    const openingStart = demokratineMokyklaPointAt(
      start,
      end,
      DEMOKRATINE_MOKYKLA_ENTRY_PROGRESS - halfFraction,
    );
    const openingEnd = demokratineMokyklaPointAt(
      start,
      end,
      DEMOKRATINE_MOKYKLA_ENTRY_PROGRESS + halfFraction,
    );
    colliders.push(
      demokratineMokyklaWallCollider(
        "river-entry-start",
        start,
        openingStart,
      ),
      demokratineMokyklaWallCollider(
        "river-entry-end",
        openingEnd,
        end,
      ),
      demokratineMokyklaWallCollider(
        "river-entry-lintel",
        openingStart,
        openingEnd,
        8.425,
        9.95,
      ),
    );
  }
  return colliders;
}

function demokratineMokyklaInteriorWallX(
  id: string,
  startX: number,
  endX: number,
  z: number,
  doorX?: number,
): readonly AuthoredLandmarkBoxCollider[] {
  const height = 4.65;
  const thickness = 0.2;
  if (doorX === undefined) {
    return [{
      kind: "box",
      id,
      centerLocalM: { x: (startX + endX) / 2, y: height / 2, z },
      sizeM: { x: endX - startX, y: height, z: thickness },
    }];
  }
  const openingWidth = 2.1;
  const openingHeight = 2.7;
  const openingStart = doorX - openingWidth / 2;
  const openingEnd = doorX + openingWidth / 2;
  return [
    {
      kind: "box",
      id: `${id}-start`,
      centerLocalM: { x: (startX + openingStart) / 2, y: height / 2, z },
      sizeM: { x: openingStart - startX, y: height, z: thickness },
    },
    {
      kind: "box",
      id: `${id}-end`,
      centerLocalM: { x: (openingEnd + endX) / 2, y: height / 2, z },
      sizeM: { x: endX - openingEnd, y: height, z: thickness },
    },
    {
      kind: "box",
      id: `${id}-lintel`,
      centerLocalM: {
        x: doorX,
        y: openingHeight + (height - openingHeight) / 2,
        z,
      },
      sizeM: { x: openingWidth, y: height - openingHeight, z: thickness },
    },
  ];
}

function demokratineMokyklaInteriorWallZ(
  id: string,
  x: number,
  startZ: number,
  endZ: number,
  doorZ?: number,
): readonly AuthoredLandmarkBoxCollider[] {
  const height = 4.65;
  const thickness = 0.2;
  if (doorZ === undefined) {
    return [{
      kind: "box",
      id,
      centerLocalM: { x, y: height / 2, z: (startZ + endZ) / 2 },
      sizeM: { x: thickness, y: height, z: endZ - startZ },
    }];
  }
  const openingWidth = 2.1;
  const openingHeight = 2.7;
  const openingStart = doorZ - openingWidth / 2;
  const openingEnd = doorZ + openingWidth / 2;
  return [
    {
      kind: "box",
      id: `${id}-start`,
      centerLocalM: { x, y: height / 2, z: (startZ + openingStart) / 2 },
      sizeM: { x: thickness, y: height, z: openingStart - startZ },
    },
    {
      kind: "box",
      id: `${id}-end`,
      centerLocalM: { x, y: height / 2, z: (openingEnd + endZ) / 2 },
      sizeM: { x: thickness, y: height, z: endZ - openingEnd },
    },
    {
      kind: "box",
      id: `${id}-lintel`,
      centerLocalM: {
        x,
        y: openingHeight + (height - openingHeight) / 2,
        z: doorZ,
      },
      sizeM: { x: thickness, y: height - openingHeight, z: openingWidth },
    },
  ];
}

function demokratineMokyklaInteriorColliders(): readonly AuthoredLandmarkBoxCollider[] {
  return [
    ...demokratineMokyklaInteriorWallX(
      "primary-classroom-north-wall",
      -9,
      -1,
      -2,
      -5,
    ),
    ...demokratineMokyklaInteriorWallX(
      "primary-classroom-south-wall",
      -9,
      -1,
      -8,
    ),
    ...demokratineMokyklaInteriorWallZ(
      "primary-classroom-west-wall",
      -9,
      -8,
      -2,
    ),
    ...demokratineMokyklaInteriorWallZ(
      "primary-classroom-east-wall",
      -1,
      -8,
      -2,
    ),
    ...demokratineMokyklaInteriorWallX(
      "project-classroom-north-wall",
      -1.5,
      6.5,
      -3,
      2.5,
    ),
    ...demokratineMokyklaInteriorWallX(
      "project-classroom-south-wall",
      -1.5,
      6.5,
      -10,
    ),
    ...demokratineMokyklaInteriorWallZ(
      "project-classroom-west-wall",
      -1.5,
      -10,
      -3,
    ),
    ...demokratineMokyklaInteriorWallZ(
      "project-classroom-east-wall",
      6.5,
      -10,
      -3,
    ),
    ...demokratineMokyklaInteriorWallX(
      "cafe-north-wall",
      9.5,
      15,
      0,
    ),
    ...demokratineMokyklaInteriorWallX(
      "cafe-south-wall",
      9.5,
      15,
      -9,
    ),
    ...demokratineMokyklaInteriorWallZ(
      "cafe-west-wall",
      9.5,
      -9,
      0,
      -4.8,
    ),
    ...demokratineMokyklaInteriorWallZ(
      "cafe-east-wall",
      15,
      -9,
      0,
    ),
  ];
}

/**
 * Enterable Demokratinė mokykla lower floor on the surveyed corner shell.
 *
 * The Neris-facing Žygimantų façade is split around the same 2.65 m opening as
 * the authored doors and sign. Contained floor/ceiling plates support its
 * vestibule, reception, two classrooms, DEMO café and curving stair. Room
 * walls and open thresholds share the generator's authored lower-floor plan.
 */
const DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME =
  demokratineMokyklaEntranceFrame();

export const VILNIUS_DEMOKRATINE_MOKYKLA_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "demokratine-mokykla",
    tileId: "1:-1",
    buildingId: DEMOKRATINE_MOKYKLA_BUILDING_ID,
    origin: Object.freeze({ x: 452.625, y: 13.09, z: -120.816 }),
    rotationYRadians: -2.152881,
    colliders: Object.freeze([
      ...demokratineMokyklaExteriorColliders(),
      ...demokratineMokyklaInteriorColliders(),
      ...[
        {
          id: "river-entry-corridor",
          x: DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.point[0] +
            DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.inward[0] * 5.25,
          z: DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.point[1] +
            DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.inward[1] * 5.25,
          width: 2.8,
          depth: 10.5,
          rotationYRadians:
            DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.rotationYRadians,
        },
        {
          id: "foyer",
          x: 6.8,
          z: -4.2,
          width: 18,
          depth: 13,
          rotationYRadians: 0,
        },
        {
          id: "classroom-corridor",
          x: -0.75,
          z: -2.6,
          width: 14.5,
          depth: 2.8,
          rotationYRadians: 0,
        },
        {
          id: "stair-hall",
          x: 5.9,
          z: 1.5,
          width: 12,
          depth: 8,
          rotationYRadians: 0,
        },
        {
          id: "north-room",
          x: -2.5,
          z: 8.9,
          width: 14,
          depth: 5.8,
          rotationYRadians: 0,
        },
        {
          id: "primary-classroom",
          x: -5,
          z: -5,
          width: 8,
          depth: 6,
          rotationYRadians: 0,
          ceilingY: 4.77,
        },
        {
          id: "project-classroom",
          x: 2.5,
          z: -6.5,
          width: 8,
          depth: 7,
          rotationYRadians: 0,
          ceilingY: 4.77,
        },
        {
          id: "demo-cafe",
          x: 12.25,
          z: -4.5,
          width: 5.5,
          depth: 9,
          rotationYRadians: 0,
          ceilingY: 4.77,
        },
      ].flatMap((plate) => [
        {
          kind: "box" as const,
          id: `${plate.id}-floor`,
          centerLocalM: { x: plate.x, y: 0.04, z: plate.z },
          sizeM: { x: plate.width, y: 0.24, z: plate.depth },
          rotationYRadians: plate.rotationYRadians,
        },
        {
          kind: "box" as const,
          id: `${plate.id}-ceiling`,
          centerLocalM: {
            x: plate.x,
            y: "ceilingY" in plate ? plate.ceilingY : 13.57,
            z: plate.z,
          },
          sizeM: { x: plate.width, y: 0.34, z: plate.depth },
          rotationYRadians: plate.rotationYRadians,
        },
      ]),
      ...[
        { outwardOffset: 1.00, top: -0.72 },
        { outwardOffset: 0.72, top: -0.47 },
        { outwardOffset: 0.44, top: -0.22 },
        { outwardOffset: 0.16, top: 0.04 },
      ].map((step, index) => ({
        kind: "box" as const,
        id: `entry-step-${index}`,
        centerLocalM: {
          x: DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.point[0] -
            DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.inward[0] *
              step.outwardOffset,
          y: (-0.94 + step.top) / 2,
          z: DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.point[1] -
            DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.inward[1] *
              step.outwardOffset,
        },
        sizeM: { x: 2.35, y: step.top + 0.94, z: 0.36 },
        rotationYRadians:
          DEMOKRATINE_MOKYKLA_ENTRANCE_FRAME.rotationYRadians,
      })),
      ...Array.from({ length: 28 }, (_, index) => {
        const progress = index / 27;
        const angle = Math.PI * -116 / 180 + Math.PI * 296 / 180 * progress;
        const top = 0.18 + 4.55 * progress;
        return {
          kind: "box" as const,
          id: `public-stair-${index}`,
          centerLocalM: {
            x: 6.6 + 2.45 * Math.cos(angle),
            y: top / 2,
            z: 0.2 + 2.45 * Math.sin(angle),
          },
          sizeM: { x: 1.72, y: top, z: 0.47 },
          rotationYRadians: angle,
        };
      }),
      {
        kind: "box" as const,
        id: "upper-landing",
        centerLocalM: { x: 3.5, y: 4.69, z: 0.9 },
        sizeM: { x: 4.2, y: 0.28, z: 3 },
      },
    ]),
  });

const SALOMEJOS_NERIES_ENTRY_EDGE = 15;
const SALOMEJOS_NERIES_ENTRY_PROGRESS = 0.5;
const SALOMEJOS_NERIES_ENTRY_WIDTH_M = 8.4;
const SALOMEJOS_NERIES_WALL_THICKNESS_M = 0.42;
// The official building base follows the lowest point of a sloping footprint.
// At the photographed river-side doors, checked-in terrain is 1.60 m above
// that datum. Keep the public floor level with the pavement instead of burying
// the doorway, furniture, and character-clearance volume in the slope.
const SALOMEJOS_NERIES_PUBLIC_FLOOR_Y = 1.6;
const SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M = 2.8;
const SALOMEJOS_NERIES_HISTORIC_CLEAR_HEIGHT_M = 2.62;

/**
 * Official GRPK outline of the school now named Vilniaus „Vyčio“ gimnazija,
 * transformed into model-local axes. The stable project ID retains the former
 * Salomėjos Nėries name requested for this upgrade; current public copy does
 * not. Edge 15 is the photographed river-side triple-door entrance.
 */
export const VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_OUTLINE_LOCAL_M =
  Object.freeze([
    [-8.874, -25.379],
    [6.9, -25.377],
    [6.901, -33.579],
    [21.842, -33.561],
    [21.86, -9.184],
    [8.832, -9.163],
    [8.225, 7.194],
    [14.577, 7.842],
    [25.991, 10.276],
    [26.641, 10.579],
    [38.961, -20.375],
    [55.184, -13.246],
    [41.577, 21.865],
    [37.577, 20.183],
    [36.074, 24.983],
    [21.899, 20.544],
    [12.299, 18.281],
    [-0.821, 17.492],
    [-18.308, 18.725],
    [-25.117, 19.678],
    [-45.08, 25.745],
    [-44.723, 27.205],
    [-59.607, 31.416],
    [-65.617, 7.559],
    [-50.592, 3.211],
    [-47.364, 16.408],
    [-33.261, 10.918],
    [-19.372, 7.668],
    [-8.737, 6.735],
    [-3.38, 6.702],
    [-2.793, -9.164],
    [-23.269, -9.167],
    [-23.267, -33.337],
    [-8.873, -33.602],
  ] as const);

function salomejosNeriesPointAt(
  start: readonly [number, number],
  end: readonly [number, number],
  progress: number,
): readonly [number, number] {
  return [
    start[0] + (end[0] - start[0]) * progress,
    start[1] + (end[1] - start[1]) * progress,
  ];
}

function salomejosNeriesEntranceFrame(): Readonly<{
  point: readonly [number, number];
  tangent: readonly [number, number];
  inward: readonly [number, number];
  rotationYRadians: number;
}> {
  const start = VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_OUTLINE_LOCAL_M[
    SALOMEJOS_NERIES_ENTRY_EDGE
  ]!;
  const end = VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_OUTLINE_LOCAL_M[
    (SALOMEJOS_NERIES_ENTRY_EDGE + 1) %
      VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_OUTLINE_LOCAL_M.length
  ]!;
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  const length = Math.hypot(deltaX, deltaZ);
  return Object.freeze({
    point: salomejosNeriesPointAt(
      start,
      end,
      SALOMEJOS_NERIES_ENTRY_PROGRESS,
    ),
    tangent: Object.freeze([deltaX / length, deltaZ / length] as const),
    inward: Object.freeze([-deltaZ / length, deltaX / length] as const),
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
  });
}

function salomejosNeriesWallHeight(edgeIndex: number): number {
  if ([0, 1, 2, 3, 4, 5, 32, 33].includes(edgeIndex)) return 13.55;
  if (
    (edgeIndex >= 6 && edgeIndex <= 14) ||
    (edgeIndex >= 19 && edgeIndex <= 27)
  ) return 10.15;
  return 7.15;
}

function salomejosNeriesWallCollider(
  id: string,
  start: readonly [number, number],
  end: readonly [number, number],
  baseY: number,
  height: number,
): AuthoredLandmarkBoxCollider {
  const deltaX = end[0] - start[0];
  const deltaZ = end[1] - start[1];
  const length = Math.hypot(deltaX, deltaZ);
  const inwardX = -deltaZ / length;
  const inwardZ = deltaX / length;
  return {
    kind: "box",
    id,
    centerLocalM: {
      x: (start[0] + end[0]) / 2 +
        inwardX * SALOMEJOS_NERIES_WALL_THICKNESS_M / 2,
      y: baseY + height / 2,
      z: (start[1] + end[1]) / 2 +
        inwardZ * SALOMEJOS_NERIES_WALL_THICKNESS_M / 2,
    },
    sizeM: {
      x: length,
      y: height,
      z: SALOMEJOS_NERIES_WALL_THICKNESS_M,
    },
    rotationYRadians: -Math.atan2(deltaZ, deltaX),
  };
}

function salomejosNeriesExteriorColliders(): readonly AuthoredLandmarkBoxCollider[] {
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  for (
    const [index, start] of
      VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_OUTLINE_LOCAL_M.entries()
  ) {
    const end = VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_OUTLINE_LOCAL_M[
      (index + 1) % VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_OUTLINE_LOCAL_M.length
    ]!;
    const height = salomejosNeriesWallHeight(index);
    if (index !== SALOMEJOS_NERIES_ENTRY_EDGE) {
      colliders.push(
        salomejosNeriesWallCollider(`outer-wall-${index}`, start, end, 0, height),
      );
      continue;
    }
    const length = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const halfFraction = SALOMEJOS_NERIES_ENTRY_WIDTH_M / length / 2;
    const openingStart = salomejosNeriesPointAt(
      start,
      end,
      SALOMEJOS_NERIES_ENTRY_PROGRESS - halfFraction,
    );
    const openingEnd = salomejosNeriesPointAt(
      start,
      end,
      SALOMEJOS_NERIES_ENTRY_PROGRESS + halfFraction,
    );
    colliders.push(
      salomejosNeriesWallCollider(
        "river-entry-start",
        start,
        openingStart,
        0,
        height,
      ),
      salomejosNeriesWallCollider(
        "river-entry-end",
        openingEnd,
        end,
        0,
        height,
      ),
      salomejosNeriesWallCollider(
        "river-entry-lintel",
        openingStart,
        openingEnd,
        SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + 3.35,
        height - SALOMEJOS_NERIES_PUBLIC_FLOOR_Y - 3.35,
      ),
    );
  }
  return colliders;
}

function salomejosNeriesInteriorWallX(
  id: string,
  startX: number,
  endX: number,
  z: number,
  doorX?: number,
  baseOffsetY = 0,
  height = 4.08,
  openingHeight = 2.62,
): readonly AuthoredLandmarkBoxCollider[] {
  const thickness = 0.18;
  if (doorX === undefined) {
    return [{
      kind: "box",
      id,
      centerLocalM: {
        x: (startX + endX) / 2,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + height / 2,
        z,
      },
      sizeM: { x: endX - startX, y: height, z: thickness },
    }];
  }
  const openingWidth = 2.1;
  const openingStart = doorX - openingWidth / 2;
  const openingEnd = doorX + openingWidth / 2;
  return [
    {
      kind: "box",
      id: `${id}-start`,
      centerLocalM: {
        x: (startX + openingStart) / 2,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + height / 2,
        z,
      },
      sizeM: { x: openingStart - startX, y: height, z: thickness },
    },
    {
      kind: "box",
      id: `${id}-end`,
      centerLocalM: {
        x: (openingEnd + endX) / 2,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + height / 2,
        z,
      },
      sizeM: { x: endX - openingEnd, y: height, z: thickness },
    },
    {
      kind: "box",
      id: `${id}-lintel`,
      centerLocalM: {
        x: doorX,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + openingHeight +
          (height - openingHeight) / 2,
        z,
      },
      sizeM: { x: openingWidth, y: height - openingHeight, z: thickness },
    },
  ];
}

function salomejosNeriesInteriorWallZ(
  id: string,
  x: number,
  startZ: number,
  endZ: number,
  baseOffsetY = 0,
  height = 4.08,
): AuthoredLandmarkBoxCollider {
  return {
    kind: "box",
    id,
    centerLocalM: {
      x,
      y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + height / 2,
      z: (startZ + endZ) / 2,
    },
    sizeM: { x: 0.18, y: height, z: endZ - startZ },
  };
}

function salomejosNeriesClassroomColliders(
  id: string,
  centerX: number,
  centerZ: number,
  width: number,
  depth: number,
  baseOffsetY = 0,
  height = 4.08,
  corridorSide: "min" | "max" = "min",
): readonly AuthoredLandmarkBoxCollider[] {
  const minX = centerX - width / 2;
  const maxX = centerX + width / 2;
  const minZ = centerZ - depth / 2;
  const maxZ = centerZ + depth / 2;
  const corridorZ = corridorSide === "min" ? minZ : maxZ;
  return [
    ...salomejosNeriesInteriorWallX(
      `${id}-corridor-wall`,
      minX,
      maxX,
      corridorZ,
      centerX,
      baseOffsetY,
      height,
      Math.min(2.38, height - 0.12),
    ),
    salomejosNeriesInteriorWallZ(
      `${id}-west-wall`,
      minX,
      minZ,
      maxZ,
      baseOffsetY,
      height,
    ),
    salomejosNeriesInteriorWallZ(
      `${id}-east-wall`,
      maxX,
      minZ,
      maxZ,
      baseOffsetY,
      height,
    ),
    {
      kind: "box",
      id: `${id}-ceiling`,
      centerLocalM: {
        x: centerX,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + height + 0.1,
        z: centerZ,
      },
      sizeM: { x: width, y: 0.2, z: depth },
    },
  ];
}

function salomejosNeriesLevelPlateColliders(
  id: string,
  centerX: number,
  centerZ: number,
  width: number,
  depth: number,
  baseOffsetY: number,
): readonly AuthoredLandmarkBoxCollider[] {
  return [
    {
      kind: "box",
      id: `${id}-floor`,
      centerLocalM: {
        x: centerX,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + 0.02,
        z: centerZ,
      },
      sizeM: { x: width, y: 0.2, z: depth },
    },
    {
      kind: "box",
      id: `${id}-ceiling`,
      centerLocalM: {
        x: centerX,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY +
          SALOMEJOS_NERIES_HISTORIC_CLEAR_HEIGHT_M + 0.1,
        z: centerZ,
      },
      sizeM: { x: width, y: 0.2, z: depth },
    },
  ];
}

function salomejosNeriesHistoricInteriorColliders():
  readonly AuthoredLandmarkBoxCollider[] {
  const colliders: AuthoredLandmarkBoxCollider[] = [];
  const roomCentersX = [-17.1, -6.1, 4.9, 15.7] as const;
  const northCenterZ = -27.72;
  const northDepth = 10.34;
  const southCenterZ = -14.86;
  const southDepth = 10.2;
  const roomWidth = 10.6;

  colliders.push(
    {
      kind: "box",
      id: "historic-courtyard-facade",
      centerLocalM: { x: -11.55, y: 6.775, z: -9.22 },
      sizeM: { x: 23.3, y: 13.55, z: 0.42 },
    },
    {
      kind: "box",
      id: "historic-link-undercroft",
      centerLocalM: { x: 3, y: 0.86, z: -5.7 },
      sizeM: { x: 5.8, y: 1.72, z: 29.4 },
    },
    {
      kind: "box",
      id: "historic-block-undercroft",
      centerLocalM: { x: -0.7, y: 0.86, z: -21.35 },
      sizeM: { x: 43.6, y: 1.72, z: 23.7 },
    },
    ...salomejosNeriesLevelPlateColliders(
      "historic-link-ground",
      3,
      -5.7,
      5.8,
      29.4,
      0,
    ),
    salomejosNeriesInteriorWallZ(
      "historic-link-wall-west",
      0.1,
      -20.4,
      9,
      0,
      SALOMEJOS_NERIES_HISTORIC_CLEAR_HEIGHT_M,
    ),
    salomejosNeriesInteriorWallZ(
      "historic-link-wall-east",
      5.9,
      -20.4,
      9,
      0,
      SALOMEJOS_NERIES_HISTORIC_CLEAR_HEIGHT_M,
    ),
  );

  for (let levelIndex = 0; levelIndex < 4; levelIndex += 1) {
    const baseOffsetY = levelIndex *
      SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M;
    const prefix = `historic-level-${levelIndex + 1}`;
    colliders.push(
      ...salomejosNeriesLevelPlateColliders(
        `${prefix}-central-corridor`,
        -0.7,
        -21.35,
        43.6,
        2.4,
        baseOffsetY,
      ),
      ...salomejosNeriesLevelPlateColliders(
        `${prefix}-cross-corridor`,
        3,
        -14.35,
        5.8,
        11.6,
        baseOffsetY,
      ),
    );
    for (const [roomIndex, roomX] of roomCentersX.entries()) {
      colliders.push(
        ...salomejosNeriesClassroomColliders(
          `${prefix}-north-room-${roomIndex + 1}`,
          roomX,
          northCenterZ,
          roomWidth,
          northDepth,
          baseOffsetY,
          SALOMEJOS_NERIES_HISTORIC_CLEAR_HEIGHT_M,
          "max",
        ),
        {
          kind: "box",
          id: `${prefix}-north-room-${roomIndex + 1}-floor`,
          centerLocalM: {
            x: roomX,
            y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + 0.02,
            z: northCenterZ,
          },
          sizeM: { x: roomWidth, y: 0.2, z: northDepth },
        },
      );
    }
    for (const [roomIndex, roomX] of roomCentersX.slice(0, 2).entries()) {
      colliders.push(
        ...salomejosNeriesClassroomColliders(
          `${prefix}-south-room-${roomIndex + 1}`,
          roomX,
          southCenterZ,
          roomWidth,
          southDepth,
          baseOffsetY,
          SALOMEJOS_NERIES_HISTORIC_CLEAR_HEIGHT_M,
          "min",
        ),
        {
          kind: "box",
          id: `${prefix}-south-room-${roomIndex + 1}-floor`,
          centerLocalM: {
            x: roomX,
            y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + 0.02,
            z: southCenterZ,
          },
          sizeM: { x: roomWidth, y: 0.2, z: southDepth },
        },
      );
    }
  }

  const stepsPerFlight = 16;
  const treadM = 0.5;
  const northZ = -20.15;
  for (let transitionIndex = 0; transitionIndex < 3; transitionIndex += 1) {
    const baseOffsetY = transitionIndex *
      SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M;
    const prefix = `historic-stair-${transitionIndex + 1}`;
    colliders.push({
      kind: "box",
      id: `${prefix}-lower-landing`,
      centerLocalM: {
        x: 13.25,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY,
        z: -19.78,
      },
      sizeM: { x: 6, y: 0.18, z: 1.25 },
    });
    for (const [guardIndex, x] of [10.15, 16.35].entries()) {
      colliders.push({
        kind: "box",
        id: `${prefix}-side-guard-${guardIndex}`,
        centerLocalM: {
          x,
          y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY + 1.85,
          z: -16.15,
        },
        sizeM: { x: 0.18, y: 3.7, z: 8.4 },
      });
    }
    const stairRunM = stepsPerFlight * treadM;
    const stairAngleRadians = Math.atan2(
      SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M,
      stairRunM,
    );
    const rampThicknessM = 0.18;
    colliders.push({
      kind: "box",
      id: `${prefix}-flight-a-ramp`,
      centerLocalM: {
        x: 13.25,
        y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY +
          SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M / 2 -
          Math.cos(stairAngleRadians) * rampThicknessM / 2,
        z: northZ + stairRunM / 2,
      },
      sizeM: {
        x: 6,
        y: rampThicknessM,
        z: Math.hypot(
          stairRunM,
          SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M,
        ),
      },
      rotationXRadians: -stairAngleRadians,
    });
    colliders.push(
      {
        kind: "box",
        id: `${prefix}-south-landing`,
        centerLocalM: {
          x: 15.72,
          y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY +
            SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M,
          z: -11.82,
        },
        sizeM: { x: 6.8, y: 0.18, z: 1.2 },
      },
      {
        kind: "box",
        id: `${prefix}-return-gallery`,
        centerLocalM: {
          x: 18.2,
          y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY +
            SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M,
          z: -16.2,
        },
        sizeM: { x: 3.4, y: 0.18, z: 8.4 },
      },
      {
        kind: "box",
        id: `${prefix}-upper-landing`,
        centerLocalM: {
          x: 18.2,
          y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + baseOffsetY +
            SALOMEJOS_NERIES_HISTORIC_STOREY_HEIGHT_M,
          z: -19.78,
        },
        sizeM: { x: 4.2, y: 0.18, z: 1.25 },
      },
    );
  }
  return colliders;
}

const SALOMEJOS_NERIES_ENTRANCE_FRAME = salomejosNeriesEntranceFrame();

/**
 * Enterable classroom school for the current Vilniaus „Vyčio“ gimnazija.
 *
 * The river-side perimeter is split around the same broad triple-door opening
 * as the authored model. Collision exposes the reception wing, a full-depth
 * link, four historic-block corridors, 24 enclosed classrooms and three
 * return-gallery stair runs. The unverified sports/event halls remain closed.
 */
export const VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "salomejos-neries-gimnazija",
    tileId: "1:3",
    buildingId: SALOMEJOS_NERIES_GIMNAZIJA_BUILDING_ID,
    origin: Object.freeze({ x: 296.043, y: 28.218, z: 809.57 }),
    rotationYRadians: -0.767945,
    colliders: Object.freeze([
      ...salomejosNeriesExteriorColliders(),
      ...salomejosNeriesClassroomColliders(
        "general-classroom",
        6,
        14.2,
        10,
        6.8,
      ),
      ...salomejosNeriesClassroomColliders(
        "media-classroom",
        -6,
        14,
        11,
        6.8,
      ),
      ...salomejosNeriesClassroomColliders(
        "project-room",
        -18.2,
        13.8,
        10.4,
        6.8,
      ),
      ...salomejosNeriesHistoricInteriorColliders(),
      ...[
        {
          id: "river-entry-corridor",
          x: SALOMEJOS_NERIES_ENTRANCE_FRAME.point[0] +
            SALOMEJOS_NERIES_ENTRANCE_FRAME.inward[0] * 4.8,
          z: SALOMEJOS_NERIES_ENTRANCE_FRAME.point[1] +
            SALOMEJOS_NERIES_ENTRANCE_FRAME.inward[1] * 4.8,
          width: 7.8,
          depth: 9.6,
          rotationYRadians:
            SALOMEJOS_NERIES_ENTRANCE_FRAME.rotationYRadians,
          ceilingY: 4.18,
        },
        {
          id: "foyer",
          x: 17,
          z: 14.55,
          width: 8.4,
          depth: 8.4,
          rotationYRadians: 0,
          ceilingY: 4.18,
        },
        {
          id: "display-corridor",
          x: -2.8,
          z: 9.35,
          width: 39,
          depth: 2.25,
          rotationYRadians: 0,
          ceilingY: 4.18,
        },
        {
          id: "general-classroom",
          x: 6,
          z: 14.2,
          width: 10,
          depth: 6.8,
          rotationYRadians: 0,
          ceilingY: 4.18,
        },
        {
          id: "media-classroom",
          x: -6,
          z: 14,
          width: 11,
          depth: 6.8,
          rotationYRadians: 0,
          ceilingY: 4.18,
        },
        {
          id: "project-room",
          x: -18.2,
          z: 13.8,
          width: 10.4,
          depth: 6.8,
          rotationYRadians: 0,
          ceilingY: 4.18,
        },
      ].flatMap((plate) => [
        {
          kind: "box" as const,
          id: `${plate.id}-floor`,
          centerLocalM: {
            x: plate.x,
            y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + 0.02,
            z: plate.z,
          },
          sizeM: { x: plate.width, y: 0.2, z: plate.depth },
          rotationYRadians: plate.rotationYRadians,
        },
        {
          kind: "box" as const,
          id: `${plate.id}-ceiling-plate`,
          centerLocalM: {
            x: plate.x,
            y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + plate.ceilingY,
            z: plate.z,
          },
          sizeM: { x: plate.width, y: 0.2, z: plate.depth },
          rotationYRadians: plate.rotationYRadians,
        },
      ]),
      {
        kind: "box" as const,
        id: "entry-threshold",
        centerLocalM: {
          x: SALOMEJOS_NERIES_ENTRANCE_FRAME.point[0] -
            SALOMEJOS_NERIES_ENTRANCE_FRAME.inward[0] * 0.52,
          y: SALOMEJOS_NERIES_PUBLIC_FLOOR_Y + 0.02,
          z: SALOMEJOS_NERIES_ENTRANCE_FRAME.point[1] -
            SALOMEJOS_NERIES_ENTRANCE_FRAME.inward[1] * 0.52,
        },
        sizeM: { x: 7.6, y: 0.14, z: 1.15 },
        rotationYRadians:
          SALOMEJOS_NERIES_ENTRANCE_FRAME.rotationYRadians,
      },
    ]),
  });

/**
 * Enterable Kablys main hall on the official former Railway Workers' Palace.
 *
 * Local +Z faces the broad Kauno Street stair and eight-column portico. Eight
 * terrain-grounded steps reach the open central doorway; the lobby continues
 * into the great hall, stage and a full-width balcony served by twin stairs.
 * Side rooms remain closed rather than inventing access beyond the published
 * public venue sequence.
 */
export const VILNIUS_KABLYS_COLLISION: AuthoredLandmarkCollisionSpec =
  Object.freeze({
    landmarkId: "kablys-cultural-venue",
    tileId: "-1:6",
    buildingId: KABLYS_BUILDING_ID,
    origin: Object.freeze({ x: -6.104, y: 53.751, z: 1680.551 }),
    rotationYRadians: -3.016872,
    colliders: Object.freeze([
      {
        kind: "box" as const,
        id: "public-floor",
        centerLocalM: { x: 0, y: 1.45, z: -0.65 },
        sizeM: { x: 33.5, y: 0.3, z: 50.089 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `side-wall-${side}`,
        centerLocalM: { x: side * 17.1, y: 6.275, z: -0.65 },
        sizeM: { x: 0.48, y: 12.55, z: 50.589 },
      })),
      {
        kind: "box" as const,
        id: "rear-wall",
        centerLocalM: { x: 0, y: 6.275, z: -25.945 },
        sizeM: { x: 34.2, y: 12.55, z: 0.48 },
      },
      {
        kind: "box" as const,
        id: "closed-rear-service-annex",
        centerLocalM: { x: -1.9, y: 5.1, z: -29.37 },
        sizeM: { x: 22, y: 10.2, z: 6.85 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `front-wall-${side}`,
        centerLocalM: { x: side * 9.825, y: 6.275, z: 24.645 },
        sizeM: { x: 14.55, y: 12.55, z: 0.5 },
      })),
      {
        kind: "box" as const,
        id: "entry-lintel",
        centerLocalM: { x: 0, y: 8.875, z: 24.645 },
        sizeM: { x: 5.1, y: 7.35, z: 0.5 },
      },
      ...Array.from({ length: 8 }, (_, index) => {
        const top = (index + 1) * 0.2;
        return {
          kind: "box" as const,
          id: `entry-step-${index}`,
          centerLocalM: {
            x: 0,
            y: top / 2,
            z: 32.645 - index - 0.5,
          },
          sizeM: {
            x: 34.2,
            y: top,
            z: 1.06,
          },
        };
      }),
      {
        kind: "box" as const,
        id: "main-stage",
        centerLocalM: { x: 0, y: 1.96, z: -21.845 },
        sizeM: { x: 18, y: 0.72, z: 7.2 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `stage-speaker-${side}`,
        centerLocalM: { x: side * 7.4, y: 4.12, z: -20.295 },
        sizeM: { x: 1.3, y: 3.6, z: 1.2 },
      })),
      {
        kind: "box" as const,
        id: "lobby-bar",
        centerLocalM: { x: -10.1, y: 2.3, z: 18 },
        sizeM: { x: 11.5, y: 1.35, z: 2 },
      },
      ...[-1, 1].flatMap((side) =>
        [7.5, -0.5, -8.5, -16.5, -24.5].map((z, index) => ({
          kind: "cylinder" as const,
          id: `hall-column-${side}-${index}`,
          centerLocalM: { x: side * 13.3, y: 6.42, z },
          heightM: 9.6,
          radiusM: 0.5,
        }))
      ),
      ...[
        { id: "centre", x: 0, width: 17 },
        { id: "west-wing", x: -14.625, width: 3.65 },
        { id: "east-wing", x: 14.625, width: 3.65 },
      ].flatMap((segment) => [
        {
          kind: "box" as const,
          id: `main-hall-balcony-${segment.id}`,
          centerLocalM: { x: segment.x, y: 7.05, z: 5.9 },
          sizeM: { x: segment.width, y: 0.34, z: 7.2 },
        },
        {
          kind: "box" as const,
          id: `balcony-front-rail-${segment.id}`,
          centerLocalM: { x: segment.x, y: 7.77, z: 2.3 },
          sizeM: { x: segment.width, y: 1.45, z: 0.16 },
        },
      ]),
      ...([-1, 1] as const).map((side) => ({
        kind: "box" as const,
        id: `balcony-landing-${side}`,
        centerLocalM: { x: side * 10.8, y: 7.05, z: 8.55 },
        sizeM: { x: 4, y: 0.34, z: 1.9 },
      })),
      ...([-1, 1] as const).flatMap((side) =>
        Array.from({ length: 24 }, (_, index) => {
          const progress = index / 23;
          const top = 1.8 + progress * 5.25;
          return {
            kind: "box" as const,
            id: `balcony-stair-${side}-${index}`,
            centerLocalM: {
              x: side * 10.8,
              y: 1.6 + (top - 1.6) / 2,
              z: -1.5 + progress * 8.6,
            },
            sizeM: {
              x: 4,
              y: top - 1.6,
              z: 0.62,
            },
          };
        })
      ),
    ]),
  });

/**
 * Walkable public hall for Vilnius Railway Station.
 *
 * Runtime local +Z faces the forecourt and local -Z continues through the
 * hall to platform one. The wing volumes remain closed, while split front and
 * rear walls leave a 6.8 m entrance axis clear. A terrain-matched six-step
 * threshold and the first platform slab connect the public route at both ends.
 * Tracks beyond platform one come from the official GRPK railway layer and are
 * rendered visual context, not simulated rail.
 */
export const VILNIUS_RAILWAY_STATION_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "vilnius-railway-station",
    tileId: "1:7",
    buildingId: VILNIUS_RAILWAY_STATION_BUILDING_ID,
    origin: Object.freeze({
      x: 449.534,
      y: 65.746,
      z: 2014.914,
    }),
    rotationYRadians: 3.707808,
    colliders: Object.freeze([
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `wing-${side}`,
        centerLocalM: { x: side * 43.5, y: 7.5, z: 0 },
        sizeM: { x: 53, y: 15, z: 36 },
      })),
      {
        kind: "box" as const,
        id: "hall-floor",
        centerLocalM: { x: 0, y: 0.89, z: 0 },
        sizeM: { x: 33, y: 0.22, z: 39 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `hall-side-${side}`,
        centerLocalM: { x: side * 16.75, y: 4.9, z: 0 },
        sizeM: { x: 0.5, y: 8, z: 40 },
      })),
      ...[-1, 1].flatMap((side) => [
        {
          kind: "box" as const,
          id: `front-wall-${side}`,
          centerLocalM: { x: side * 10.25, y: 4.9, z: 19.75 },
          sizeM: { x: 13.7, y: 8, z: 0.5 },
        },
        {
          kind: "box" as const,
          id: `rear-wall-${side}`,
          centerLocalM: { x: side * 10.25, y: 4.9, z: -19.75 },
          sizeM: { x: 13.7, y: 8, z: 0.5 },
        },
      ]),
      {
        kind: "box" as const,
        id: "front-lintel",
        centerLocalM: { x: 0, y: 8.2, z: 19.75 },
        sizeM: { x: 6.8, y: 3.4, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "rear-lintel",
        centerLocalM: { x: 0, y: 8.2, z: -19.75 },
        sizeM: { x: 6.8, y: 3.4, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "hall-ceiling",
        centerLocalM: { x: 0, y: 17.75, z: 0 },
        sizeM: { x: 33, y: 0.45, z: 39 },
      },
      ...Array.from({ length: 6 }, (_, index) => {
        const base = -1.1;
        const top = -0.55 + index * 0.3;
        return {
          kind: "box" as const,
          id: `entry-step-${index}`,
          centerLocalM: {
            x: 0,
            y: (top + base) / 2,
            z: 28.5 - index * 1.5,
          },
          sizeM: { x: 18 - index * 0.6, y: top - base, z: 1.8 },
        };
      }),
      {
        kind: "box" as const,
        id: "platform-one",
        centerLocalM: { x: 0, y: 0.619, z: -22 },
        sizeM: { x: 238, y: 0.42, z: 4 },
      },
      {
        kind: "box" as const,
        id: "ticket-counter",
        centerLocalM: { x: 13.7, y: 1.55, z: 4 },
        sizeM: { x: 2.2, y: 1.25, z: 12 },
      },
      {
        kind: "box" as const,
        id: "locker-bank",
        centerLocalM: { x: -14.3, y: 2.1, z: 5 },
        sizeM: { x: 2, y: 2.45, z: 8.5 },
      },
      ...[-7, 0.5, 8].flatMap((blenderY, row) =>
        [-1, 1].map((side) => ({
          kind: "box" as const,
          id: `bench-${row}-${side}`,
          centerLocalM: { x: side * 5.8, y: 1.28, z: -blenderY },
          sizeM: { x: 4.6, y: 1.9, z: 1 },
        }))
      ),
    ]),
  });

/**
 * Walkable passenger hall for Vilnius Bus Station. Runtime local +Z opens to
 * the numbered intercity platforms and local -Z opens toward the rail-station
 * pedestrian connection. Closed commercial wings preserve the official GRPK
 * footprint while an 8 m public axis remains unobstructed through the hall.
 */
export const VILNIUS_BUS_STATION_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "vilnius-bus-station",
    tileId: "1:7",
    buildingId: VILNIUS_BUS_STATION_BUILDING_ID,
    origin: Object.freeze({ x: 279.11, y: 61.282, z: 1962.813 }),
    rotationYRadians: 2.137854,
    colliders: Object.freeze([
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `closed-wing-${side}`,
        centerLocalM: { x: side * 38.5, y: 4.75, z: 0.5 },
        sizeM: { x: 11, y: 9.5, z: 40 },
      })),
      {
        kind: "box" as const,
        id: "passenger-hall-floor",
        centerLocalM: { x: 0, y: -0.06, z: 0.5 },
        sizeM: { x: 66, y: 0.24, z: 40 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `passenger-hall-side-${side}`,
        centerLocalM: { x: side * 33.25, y: 4.4, z: 0.5 },
        sizeM: { x: 0.5, y: 8.8, z: 40 },
      })),
      ...[17.75, -20.75].flatMap((z, face) => [
        {
          kind: "box" as const,
          id: `${face === 0 ? "platform" : "city"}-wall-west`,
          centerLocalM: { x: -16, y: 4.4, z },
          sizeM: { x: 34, y: 8.8, z: 0.5 },
        },
        {
          kind: "box" as const,
          id: `${face === 0 ? "platform" : "city"}-wall-east`,
          centerLocalM: { x: 21, y: 4.4, z },
          sizeM: { x: 24, y: 8.8, z: 0.5 },
        },
        {
          kind: "box" as const,
          id: `${face === 0 ? "platform" : "city"}-lintel`,
          centerLocalM: { x: 5, y: 7.5, z },
          sizeM: { x: 8, y: 2.6, z: 0.5 },
        },
      ]),
      {
        kind: "box" as const,
        id: "passenger-hall-ceiling",
        centerLocalM: { x: 0, y: 9.25, z: 0.5 },
        sizeM: { x: 66, y: 0.5, z: 40 },
      },
      {
        kind: "box" as const,
        id: "ticket-counter",
        centerLocalM: { x: 25.4, y: 1.1, z: -2 },
        sizeM: { x: 2.6, y: 2.2, z: 14 },
      },
      ...[-9, 0, 9].map((z, index) => ({
        kind: "box" as const,
        id: `waiting-bench-row-${index}`,
        centerLocalM: { x: -9, y: 0.7, z },
        sizeM: { x: 11, y: 1.4, z: 1.25 },
      })),
    ]),
  });

/**
 * Traversable public route through Vilnius Airport's terminal generations.
 *
 * Local origin is the centre of the 2025 departures hall. The route begins at
 * its landside glass doors, climbs to the security floor, continues at ground
 * level through the glazed connector and T2-T3/VNO Plaza, then exits through
 * the historic 1954 arrivals frontage. Secure side rooms stay closed.
 */
export const VILNIUS_AIRPORT_TERMINAL_COLLISION:
  AuthoredLandmarkCollisionSpec = Object.freeze({
    landmarkId: "vilnius-airport-terminal-complex",
    tileId: "1:19",
    buildingId: VILNIUS_AIRPORT_TERMINAL_BUILDING_ID,
    origin: Object.freeze({ x: 246, y: 110, z: 4914 }),
    rotationYRadians: 0,
    colliders: Object.freeze([
      {
        kind: "box" as const,
        id: "departures-floor",
        centerLocalM: { x: 0, y: 0.14, z: 0 },
        sizeM: { x: 112, y: 0.28, z: 81 },
      },
      ...[-1, 1].flatMap((side) => [
        {
          kind: "box" as const,
          id: `departures-west-wall-${side}`,
          centerLocalM: { x: -56.5, y: 9, z: side * 24 },
          sizeM: { x: 0.5, y: 18, z: 34 },
        },
        {
          kind: "box" as const,
          id: `departures-east-wall-${side}`,
          centerLocalM: { x: 56.5, y: 9, z: side * 24.5 },
          sizeM: { x: 0.5, y: 18, z: 33 },
        },
        {
          kind: "box" as const,
          id: `departures-south-wall-${side}`,
          centerLocalM: { x: side * 33.25, y: 9, z: 41 },
          sizeM: { x: 46.5, y: 18, z: 0.5 },
        },
      ]),
      {
        kind: "box" as const,
        id: "departures-north-wall",
        centerLocalM: { x: 0, y: 9, z: -41 },
        sizeM: { x: 113, y: 18, z: 0.5 },
      },
      ...[
        ["west", -56.5, 0, 0.5, 14],
        ["east", 56.5, 0, 0.5, 12],
      ].map(([id, x, z, sizeX, sizeZ]) => ({
        kind: "box" as const,
        id: `departures-${id}-lintel`,
        centerLocalM: { x: Number(x), y: 11.1, z: Number(z) },
        sizeM: { x: Number(sizeX), y: 13.8, z: Number(sizeZ) },
      })),
      {
        kind: "box" as const,
        id: "departures-south-lintel",
        centerLocalM: { x: 0, y: 11.1, z: 41 },
        sizeM: { x: 20, y: 13.8, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "departures-roof",
        centerLocalM: { x: 0, y: 18.25, z: 0 },
        sizeM: { x: 115, y: 0.5, z: 84 },
      },
      {
        kind: "box" as const,
        id: "departures-upper-floor",
        centerLocalM: { x: 28, y: 7.19, z: 0 },
        sizeM: { x: 54, y: 0.28, z: 74 },
      },
      ...Array.from({ length: 27 }, (_, index) => {
        const height = (index + 1) * (7.05 / 27);
        return {
          kind: "box" as const,
          id: `departures-stair-${index}`,
          centerLocalM: {
            x: -12 + index * 0.48,
            y: 0.18 + height / 2,
            z: 21,
          },
          sizeM: { x: 0.56, y: height, z: 5.6 },
        };
      }),
      {
        kind: "box" as const,
        id: "connector-floor",
        centerLocalM: { x: 0, y: 0.14, z: 60 },
        sizeM: { x: 36, y: 0.28, z: 40 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `connector-wall-${side}`,
        centerLocalM: { x: side * 18, y: 5, z: 60 },
        sizeM: { x: 0.5, y: 10, z: 40 },
      })),
      {
        kind: "box" as const,
        id: "connector-roof",
        centerLocalM: { x: 0, y: 10.35, z: 60 },
        sizeM: { x: 36, y: 0.7, z: 40 },
      },
      {
        kind: "box" as const,
        id: "existing-terminal-floor",
        centerLocalM: { x: 24, y: 0.14, z: 121 },
        sizeM: { x: 142, y: 0.28, z: 84 },
      },
      ...[-1, 1].flatMap((side) => [
        {
          kind: "box" as const,
          id: `existing-west-wall-${side}`,
          centerLocalM: { x: -47, y: 6.5, z: 121 + side * 24 },
          sizeM: { x: 0.5, y: 13, z: 36 },
        },
        {
          kind: "box" as const,
          id: `existing-east-wall-${side}`,
          centerLocalM: { x: 95, y: 6.5, z: 121 + side * 24 },
          sizeM: { x: 0.5, y: 13, z: 36 },
        },
      ]),
      {
        kind: "box" as const,
        id: "existing-north-west-wall",
        centerLocalM: { x: -28.5, y: 6.5, z: 79 },
        sizeM: { x: 37, y: 13, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "existing-north-east-wall",
        centerLocalM: { x: 52.5, y: 6.5, z: 79 },
        sizeM: { x: 85, y: 13, z: 0.5 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `existing-south-wall-${side}`,
        centerLocalM: { x: 24 + side * 40.5, y: 6.5, z: 163 },
        sizeM: { x: 61, y: 13, z: 0.5 },
      })),
      {
        kind: "box" as const,
        id: "existing-north-lintel",
        centerLocalM: { x: 0, y: 8.6, z: 79 },
        sizeM: { x: 20, y: 8.8, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "existing-south-lintel",
        centerLocalM: { x: 24, y: 8.6, z: 163 },
        sizeM: { x: 20, y: 8.8, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "existing-terminal-roof",
        centerLocalM: { x: 24, y: 13.25, z: 121 },
        sizeM: { x: 144, y: 0.5, z: 86 },
      },
      {
        kind: "box" as const,
        id: "historic-link-floor",
        centerLocalM: { x: 24, y: 0.14, z: 165.75 },
        sizeM: { x: 14, y: 0.28, z: 6.5 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `historic-link-wall-${side}`,
        centerLocalM: { x: 24 + side * 7, y: 3.1, z: 165.75 },
        sizeM: { x: 0.5, y: 6.2, z: 6.5 },
      })),
      {
        kind: "box" as const,
        id: "historic-link-roof",
        centerLocalM: { x: 24, y: 6.45, z: 165.75 },
        sizeM: { x: 14, y: 0.5, z: 6.5 },
      },
      {
        kind: "box" as const,
        id: "historic-floor",
        centerLocalM: { x: 24, y: 0.14, z: 184 },
        sizeM: { x: 91, y: 0.28, z: 30 },
      },
      ...[-1, 1].map((side) => ({
        kind: "box" as const,
        id: `historic-side-wall-${side}`,
        centerLocalM: { x: 24 + side * 46, y: 7, z: 184 },
        sizeM: { x: 0.5, y: 14, z: 31 },
      })),
      ...[-1, 1].flatMap((side) => [
        {
          kind: "box" as const,
          id: `historic-front-wall-${side}`,
          centerLocalM: { x: 24 + side * 25.25, y: 7, z: 199.5 },
          sizeM: { x: 41.5, y: 14, z: 0.5 },
        },
        {
          kind: "box" as const,
          id: `historic-rear-wall-${side}`,
          centerLocalM: { x: 24 + side * 26.5, y: 7, z: 168.5 },
          sizeM: { x: 39, y: 14, z: 0.5 },
        },
      ]),
      {
        kind: "box" as const,
        id: "historic-front-lintel",
        centerLocalM: { x: 24, y: 9.2, z: 199.5 },
        sizeM: { x: 9, y: 9.6, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "historic-rear-lintel",
        centerLocalM: { x: 24, y: 9.2, z: 168.5 },
        sizeM: { x: 14, y: 9.6, z: 0.5 },
      },
      {
        kind: "box" as const,
        id: "historic-roof",
        centerLocalM: { x: 24, y: 14.25, z: 184 },
        sizeM: { x: 92, y: 0.5, z: 31 },
      },
      {
        kind: "box" as const,
        id: "historic-info-desk",
        centerLocalM: { x: 4, y: 0.75, z: 185.5 },
        sizeM: { x: 9, y: 1.25, z: 2.5 },
      },
      {
        kind: "box" as const,
        id: "historic-cafe-counter",
        centerLocalM: { x: 44, y: 0.72, z: 185.5 },
        sizeM: { x: 10, y: 1.2, z: 2.4 },
      },
      ...[177, 191].flatMap((z, row) =>
        [5, 43].map((x, side) => ({
          kind: "box" as const,
          id: `historic-seat-${row}-${side}`,
          centerLocalM: { x, y: 0.59, z },
          sizeM: { x: 6, y: 0.82, z: 1.2 },
        }))
      ),
      ...[4890, 4906, 4922, 4938].map((worldZ, index) => ({
        kind: "box" as const,
        id: `check-in-island-${index}`,
        centerLocalM: { x: -29, y: 0.77, z: worldZ - 4914 },
        sizeM: { x: 12.5, y: 1.18, z: 2.4 },
      })),
      ...[320, 340, 360].map((worldX, index) => ({
        kind: "box" as const,
        id: `baggage-carousel-${index}`,
        centerLocalM: { x: worldX - 246, y: 0.64, z: 136 },
        sizeM: { x: 12.5, y: 0.92, z: 3.4 },
      })),
    ]),
  });

export function authoredLandmarkCollisionForBuilding(
  tileId: string,
  buildingId: string,
): AuthoredLandmarkCollisionSpec | null {
  return [
    VILNIUS_CATHEDRAL_COLLISION,
    VILNIUS_GEDIMINAS_CASTLE_COLLISION,
    VILNIUS_GRAND_DUKES_PALACE_COLLISION,
    VILNIUS_GATE_OF_DAWN_COLLISION,
    VILNIUS_UNIVERSITY_LIBRARY_COLLISION,
    VILNIUS_LUKISKES_PRISON_COLLISION,
    ...LUKISKES_PRISON_SUPPRESSED_COLLISIONS,
    VILNIUS_PRESIDENTIAL_PALACE_COLLISION,
    VILNIUS_TOWN_HALL_COLLISION,
    VILNIUS_LIETUVOS_BANKAS_COLLISION,
    VILNIUS_TV_TOWER_COLLISION,
    VILNIUS_GEDIMINO_9_COLLISION,
    VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION,
    VILNIUS_HALES_MARKET_COLLISION,
    VILNIUS_MINDAUGO_MAXIMA_COLLISION,
    VILNIUS_DEMOKRATINE_MOKYKLA_COLLISION,
    VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_COLLISION,
    VILNIUS_KABLYS_COLLISION,
    VILNIUS_BUS_STATION_COLLISION,
    VILNIUS_RAILWAY_STATION_COLLISION,
    VILNIUS_AIRPORT_TERMINAL_COLLISION,
  ].find((landmark) =>
    tileId === landmark.tileId && buildingId === landmark.buildingId
  ) ?? null;
}
